import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {Table} from 'primeng/table';
import {SortEvent} from 'primeng/api';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {
    CpProvisionAgentConfigurationService
} from '../../../../../service/api/cp-provision-agent-configuration.service';
import {
    ProvisionAgentConfigurationAssignmentDto,
    ProvisionAgentConfigurationComponentDto,
    ProvisionAgentConfigurationDimensionDto,
    ProvisionAgentConfigurationDto,
    ProvisionAgentConfigurationRowDto
} from '../../../../../model/dto/api/provision-agent-configuration.dto';
import {ProvisionLiabilitySource} from '../../../../../enum/provision-liability-source.enum';
import {ProvisionMatrixCalcMode} from '../../../../../enum/provision-matrix-calc-mode.enum';
import {ProvisionConfigurationOrigin} from '../../../../../enum/provision-configuration-origin.enum';
import {ProvisionConfigurationAxis} from '../../../../../enum/provision-configuration-axis.enum';

/**
 * BUNDLING NOTE for the view-model interfaces below — the same deliberate deviation from
 * fe_core_standards STRUCT-002 ("one interface per file") this module already takes for its API DTOs
 * (`provision-config-check-result.dto.ts`, which trips the identical check with the identical
 * reasoning). Both types are the render model of exactly ONE component, they are built by it and
 * consumed by nothing else, and separating them into files of their own would put the component's
 * internals two directories from the component. They stay here.
 */

/**
 * The empty candidate list, shared by every display row that has none.
 *
 * One frozen instance rather than a fresh `[]` per row: `unreachableCandidates` is read by an
 * `@if` in the detail template, and a new array per row would be new work for no new information.
 */
const NO_UNREACHABLE_CANDIDATES: ReadonlyArray<ProvisionAgentConfigurationRowDto> = Object.freeze([]);

/**
 * The empty inheritance chain, shared by every assignment whose chain the endpoint could not build.
 *
 * Same reasoning as {@link NO_UNREACHABLE_CANDIDATES}: the array is read by an `@if` and a fresh
 * `[]` per assignment would be new work for no new information.
 */
const NO_PATH_STEPS: ReadonlyArray<CpConfigurationPathStep> = Object.freeze([]);

/**
 * Prefix of the sort field of a DIMENSION column, e.g. `dim:contractType`.
 *
 * The dimension columns are DATA (`occupiedDimensions`), so their sort fields have to be built from
 * the dimension key. The prefix keeps them apart from the fixed fields ("factor", "term", …) and
 * makes an accidental collision with a dimension named "factor" impossible.
 */
const DIMENSION_SORT_FIELD_PREFIX: string = 'dim:';

/** Sort field of the Faktor column — the pair (Berechnungsart, Zahl), never the rendered text. */
const SORT_FIELD_FACTOR: string = 'factor';

/** Sort field of the Laufzeit column — the pair (von, bis) in months. */
const SORT_FIELD_TERM: string = 'term';

/** Sort field of the Eintrittsalter column — the pair (von, bis) in years. */
const SORT_FIELD_ENTRY_AGE: string = 'entryAge';

/** Sort field of the Haftung column — the resolved Haftungsart name. */
const SORT_FIELD_LIABILITY: string = 'liability';

/** Sort field of the Achse column — AGENT before GROUP. */
const SORT_FIELD_AXIS: string = 'axis';

/** Sort field of the "Herkunft des Werts" column — OWN before INHERITED, then the matrix name. */
const SORT_FIELD_ORIGIN: string = 'origin';

/**
 * ONE step of the K-7 inheritance chain, ready to render as a chip.
 *
 * Built once per ASSIGNMENT (never per display row): the chain belongs to the assignment, and only
 * WHICH of its steps is the active one differs from row to row — that is
 * {@link CpConfigurationDisplayRow.activePathIndex}.
 */
export interface CpConfigurationPathStep {

    /** The matrix name, exactly as the endpoint sent it. */
    name: string;

    /**
     * Id of that matrix, when the backend delivers the parallel id list. `undefined` means the step
     * cannot be opened — it is then rendered as a plain chip, without a cursor and without a link
     * tooltip, rather than as a link that would go nowhere.
     */
    matrixId?: number;

    /** German tooltip of this chip; differs for the first step (the assigned matrix). */
    tooltip: string;
}

/**
 * The active sort of ONE table — the state that makes the deviation from the evaluation order
 * visible and reversible.
 *
 * `null` on a block means the table stands in the order the calculation evaluates it, which is the
 * pane's default and its whole point.
 */
export interface CpConfigurationSortState {

    /** The sort field PrimeNG reported — one of the constants above. */
    field: string;

    /** 1 ascending, -1 descending, as PrimeNG's `SortEvent.order`. */
    order: number;

    /** German label of the sorted column, for the chip and the introductory sentence. */
    columnLabel: string;

    /** The full German chip text, built once when the sort is applied. */
    chipLabel: string;

    /** Tooltip of that chip — says what the key actually is, where it is not obvious. */
    chipTooltip: string;
}

/**
 * THE "alles Übrige" line of a component block — the universal fallback rate, shown ONCE.
 *
 * The root matrix of the inheritance tree contributes one rate that every assignment inherits, so
 * the payload carries the SAME physical record under all of them (measured: one `matrixRowId` in
 * 190 of 388 row instances). Rendered per assignment it read as 190 separate statements of "Faktor
 * 0"; it is one statement, and it belongs at the bottom of the block where the lowest priority is.
 */
export interface CpConfigurationUniversalFallback {

    /** The row itself — factor, Berechnungsart, Haftung and origin matrix come from here. */
    row: ProvisionAgentConfigurationRowDto;

    /**
     * Set when this is ANOTHER Vermittlergruppe's fallback, collected under
     * `includeGroupComparison`. Undefined for the agent's own group.
     */
    comparisonGroupName?: string;

    /**
     * Where this line's Haftungsart comes from — the same four German words the table cell and the
     * row expansion use: Matrix, Zuweisung, generell, keine Haftung.
     *
     * This line USED to show the Haftungsart without its source, and it was the last place in the
     * pane that did. The client's annotation reads "Hier brauchen wir die Herkunft der Haftungsart",
     * and it applies here in particular: measured on clone/rathje, the "alles Übrige" rows are where
     * the "generell" cases sit — on account 9000001068 ALL 108 rows carrying
     * `liabilityAssignmentDefaultRow` are universal fallbacks, so the one word this project exists to
     * make visible was invisible in the one place it always applies.
     */
    liabilityOriginTitle: string;

    /** The explaining sentence behind that word, shown as its tooltip. */
    liabilityOriginDetail: string;

    /** True when the source is known but the Haftungsart itself is not nameable (inactive type). */
    liabilityTypeMissing: boolean;
}

/**
 * ONE display row of a component block — an assignment paired with ONE of its resolved matrix rows.
 *
 * The table cannot be "one row per assignment": factor, Berechnungsart, Laufzeitband, Haftung and
 * origin all live on the MATRIX ROW, and an assignment carries a band of them (requirement §7.4 —
 * a child matrix does not hide the parent's rows, the candidates are concatenated). So an
 * assignment with three bands occupies three lines, its dimension cells drawn once on the first.
 *
 * The array of these is built in DELIVERY ORDER and never sorted (see
 * {@link CpProvisionAgentConfigurationComponent.buildDisplayRows}).
 */
export interface CpConfigurationDisplayRow {

    /** Stable key for the p-table `dataKey`, so row expansion survives a re-render. */
    rowKey: string;

    /** The assignment this line belongs to. */
    assignment: ProvisionAgentConfigurationAssignmentDto;

    /** The resolved matrix row rendered on this line. */
    row: ProvisionAgentConfigurationRowDto;

    /** True on the FIRST line of an assignment — the only one that draws the dimension cells. */
    leadRow: boolean;

    /** Count of lines this assignment occupies; shown as the band hint on the lead row. */
    assignmentRowCount: number;

    /**
     * The sentence a CONTINUATION line carries in place of the dimension cells the lead line drew.
     *
     * Empty on the lead line. It exists because a blank cell was READ AS A DEFECT: in the same
     * column an unrestricted dimension prints the word "alle", so a neighbouring empty cell looks
     * like a missing value rather than "belongs to the line above". The line therefore names its
     * own reason for existing — a second term band of the SAME assignment, which is shown because
     * the view has no contract and cannot know which band wins.
     */
    continuationHint: string;

    /**
     * Set on a line that shows what ANOTHER Vermittlergruppe gets for the same assignment
     * (`includeGroupComparison`). Undefined on the agent's own lines.
     */
    comparisonGroupName?: string;

    /**
     * Everything about this line the row filter searches, concatenated ONCE at build time.
     *
     * Precomputed for the same reason the rest of this view model is: `[globalFilterFields]` makes
     * p-table read these fields on every keystroke over every row, and a getter — or a filter that
     * walked `assignment.dimensions` per row per keystroke — would do that work again and again.
     * A flat string is built once and compared with one `indexOf`.
     *
     * PrimeNG's `contains` lower-cases both sides itself, so the ORIGINAL casing is kept here; it
     * costs nothing and keeps the field readable when debugging.
     */
    searchText: string;

    /**
     * The candidates of this assignment that can NEVER be selected — shown in the row expansion,
     * never as table rows.
     *
     * Carried on EVERY display row of the assignment (one shared array, not a copy per row) so
     * that expanding any line of the assignment reveals them; they belong to the assignment, not
     * to one of its lines. Empty for comparison rows, whose candidates belong to another group.
     *
     * They are moved rather than dropped: the rows exist in the configuration, and a pane whose
     * whole purpose is "warum bekomme ich das" may not quietly omit part of the answer.
     */
    unreachableCandidates: ReadonlyArray<ProvisionAgentConfigurationRowDto>;

    /**
     * The dimension cell texts of this row, ALIGNED WITH `block.dimensionColumns` by index.
     *
     * The template indexes into this array instead of calling a resolver per cell per change
     * detection pass. Measured before this change: `dimensionValue()` ran 23,450 times per pass,
     * each doing an `Array.find()` over the assignment's dimensions — by far the most expensive
     * thing on the page.
     *
     * ORDERING IS A CONTRACT: index i is the value for `block.dimensionColumns[i]`, built by
     * walking the SAME array the header walks, so the columns cannot drift apart. The cells depend
     * only on the ASSIGNMENT, so every display row of one assignment shares one array instance.
     *
     * An empty string means "not occupied", which the template renders as "alle" — exactly what
     * the resolver returned before.
     */
    dimensionCells: string[];

    /** German Laufzeit band text, or an empty string. Precomputed {@link termBandLabel}. */
    termBandText: string;

    /** German Eintrittsalter band text, or an empty string. Precomputed {@link entryAgeBandLabel}. */
    entryAgeBandText: string;

    /** Precomputed {@link isAgentAxis} — true for a personal row. Drives a class and a severity. */
    agentAxis: boolean;

    /**
     * German statement shown INSTEAD of a factor when this assignment has no rate of its own.
     *
     * Set on the "kein Wert" line — an assignment that arrived without rows, and now also one
     * whose ONLY row was the universal fallback that has been lifted to the bottom of the block
     * (measured: 30 of 190 assignments). Such an assignment must keep its line and its dimension
     * cells: "Baloise / Unfallversicherung" IS the information, and a vanished or blank row would
     * destroy it. Empty on every ordinary row.
     *
     * Precomputed in {@link CpProvisionAgentConfigurationComponent.buildBlocks} — after the rows
     * are built, because the wording depends on whether the block ended up with a fallback line.
     */
    noRateHint?: string;

    /**
     * The K-7 chain of this row's assignment as renderable chips — ONE shared array instance per
     * assignment, exactly like {@link dimensionCells}.
     *
     * Empty when the endpoint delivered no chain, which happens when the assigned matrix is
     * inactive, deleted or foreign: the pane then says so in words instead of showing nothing (the
     * case that used to be invisible, because the assignment's warning was rendered INSIDE the
     * chain block).
     */
    pathSteps: ReadonlyArray<CpConfigurationPathStep>;

    /**
     * Index in {@link pathSteps} of the step the displayed value physically comes from, or -1.
     *
     * THE one row-specific part of the chain: the chain belongs to the assignment, the origin to
     * the row. Resolved by matrix id where the backend delivers ids, and by matrix name otherwise —
     * see {@link CpProvisionAgentConfigurationComponent.resolveActivePathIndex}.
     */
    activePathIndex: number;

    /**
     * First line of "Herkunft der Haftungsart": Matrix, Zuweisung, generell or keine Haftung.
     *
     * NEVER empty — the section is rendered for every row, including the one whose source is
     * `null`. That case means "keine Haftung" (no liability period, no chargeback, engine message
     * M-041) and it used to make the whole section disappear, which reads like a missing value
     * rather than like the statement it is.
     */
    liabilityOriginTitle: string;

    /** Second line of the same section — what that word means for this row. Never empty. */
    liabilityOriginDetail: string;

    /**
     * True when the source of the Haftungsart is known but its NAME is not — an inactive
     * Haftungsart. The value cell stays empty and the section says why, instead of falling back to
     * "—", which would read as "keine Haftung" and is a different statement.
     */
    liabilityTypeMissing: boolean;

    /**
     * True when this row's business period does NOT cover the key date.
     *
     * Precomputed against `false` explicitly, so an absent flag is not read as "invalid". Measured
     * on clone/rathje 2026-09-02: 0 of 10.443 rows: the marker is insurance against a code path
     * that exists (the runtime index filters on `entityStatus`, not on the period), not the repair
     * of a visible defect.
     */
    invalidAtKeyDate: boolean;
}

/**
 * ONE rendered component block — a Provisionskomponente with its table already assembled.
 *
 * Built ONCE per load in {@link CpProvisionAgentConfigurationComponent.buildBlocks}, deliberately
 * not from the template. A `[value]="buildRows(...)"` binding would hand p-table a NEW array on
 * every change-detection pass, which remounts the table, drops the open row expansions and makes
 * the pane quietly expensive. The view model is data, so it is computed where data is computed.
 */
export interface CpConfigurationBlock {

    /** The component this block renders. */
    component: ProvisionAgentConfigurationComponentDto;

    /** The dimension columns of THIS component, in backend order — the data-driven column set. */
    dimensionColumns: ProvisionAgentConfigurationDimensionDto[];

    /**
     * The sort field of each dimension column, INDEX-PARALLEL to {@link dimensionColumns}.
     *
     * Built once, because a `[pSortableColumn]="'dim:' + column.key"` in the template would build a
     * string on every change-detection pass over every column of every table.
     */
    dimensionSortFields: string[];

    /** German column label per sort field — the chip and the introductory sentence name the column. */
    sortColumnLabels: { [field: string]: string };

    /**
     * Per sort field: whether it is a ROW property (Faktor, Laufzeit, Haftung …) rather than an
     * assignment property (the dimensions).
     *
     * Decides the wording of the chip only. The sort itself always takes the group extremum, which
     * for an assignment-level column is trivially the single value the whole group carries.
     */
    rowLevelSortFields: { [field: string]: boolean };

    /** The display rows shown by default: the assignments that remunerate something. */
    payingRows: CpConfigurationDisplayRow[];

    /**
     * EVERY paying display row of this block, in delivery order, frozen at build time.
     *
     * The complete set — the visibility switch never touches it. It is the denominator of the
     * "X von Y Zeilen" line and the source both other arrays are derived from.
     */
    payingRowsAll: ReadonlyArray<CpConfigurationDisplayRow>;

    /**
     * The currently VISIBLE paying rows in delivery order — {@link payingRowsAll} minus whatever the
     * visibility switch hides.
     *
     * THE reference the sort always starts from, and what "Prüfreihenfolge" restores. Sorting out of
     * the already re-ordered array instead would make the result depend on the previous sort — the
     * tie-break "everything equal stays in evaluation order" would decay into "stays wherever it was
     * last put". Frozen because nothing may ever write to it; it is REPLACED, never mutated, when
     * the visibility changes.
     */
    payingRowsEvaluationOrder: ReadonlyArray<CpConfigurationDisplayRow>;

    /** The display rows folded away behind "N Kombinationen ohne Vergütung anzeigen". */
    zeroRows: CpConfigurationDisplayRow[];

    /** Every zero display row of this block, frozen — same contract as its paying counterpart. */
    zeroRowsAll: ReadonlyArray<CpConfigurationDisplayRow>;

    /** The currently visible zero rows in delivery order — same contract as its paying counterpart. */
    zeroRowsEvaluationOrder: ReadonlyArray<CpConfigurationDisplayRow>;

    /**
     * How many ASSIGNMENTS of this block the visibility switch currently hides, over both tables.
     * 0 while the switch is off — and 0 while it is on but this block has nothing to hide, which is
     * why the chip is bound to this number and not to the switch.
     */
    hiddenAssignmentCount: number;

    /** How many display ROWS those assignments occupied, over both tables. */
    hiddenRowCount: number;

    /**
     * The same two numbers for the PAYING table alone.
     *
     * Kept apart from the block-wide pair on purpose: the caption chip sits directly beside the
     * "X von Y Zeilen" line of that one table, and a chip saying "3 ausgeblendet" next to a counter
     * saying "88 von 88" would be two true statements that read as a contradiction. The chip
     * therefore counts what ITS table hides; the block heading counts the whole section.
     */
    hiddenPayingAssignmentCount: number;

    /** How many rows of the paying table are currently hidden. Drives the "X von Y" line. */
    hiddenPayingRowCount: number;

    /**
     * True when the visibility switch hides this WHOLE component section, because after it nothing
     * remunerating is left in it.
     *
     * Set only when the paying table is empty AND the block's "alles Übrige" line carries no rate
     * either — a non-zero catch-all IS money and keeps its section, however empty the table looks.
     * The hidden sections are NAMED above the first block; they never vanish silently.
     */
    hiddenBlock: boolean;

    /** The active sort of the paying table, or `null` while it stands in evaluation order. */
    payingSort: CpConfigurationSortState;

    /** The active sort of the Nullkombinationen table, or `null`. */
    zeroSort: CpConfigurationSortState;

    /** How many ASSIGNMENTS are folded away — the N in the toggle's label. */
    zeroAssignmentCount: number;

    /**
     * Whether this block's zero rows are unfolded — a plain mirror of
     * {@link CpProvisionAgentConfigurationComponent.zeroRowsExpanded}.
     *
     * The map remains the source of truth, because it must SURVIVE a reload (changing the key date
     * rebuilds every block, and an unfolded zero block has to stay unfolded). This field exists so
     * the template reads a boolean instead of calling a method that builds a `String(componentId)`
     * key on every change-detection pass. Both are written in {@link toggleZeroBlock}.
     */
    zeroBlockExpanded: boolean;

    /** Precomputed toggle caption for the folded state — no string built in a binding. */
    zeroToggleLabelShow: string;

    /** Precomputed toggle caption for the unfolded state. */
    zeroToggleLabelHide: string;

    /** How many distinct ASSIGNMENTS the paying table shows. Excludes the folded zero block. */
    payingAssignmentCount: number;

    /** How many display ROWS the paying table shows — the denominator of the filter counter. */
    payingRowCount: number;

    /**
     * The "alles Übrige" lines of this block — normally exactly one, or one per compared group
     * when `includeGroupComparison` is on. Empty when the payload carries no universal fallback.
     *
     * Deduplicated across the whole block: the same record arrives under every assignment and is
     * collected once, keyed by matrix row and group.
     */
    universalFallbackRows: CpConfigurationUniversalFallback[];

    /**
     * How many candidates of the paying assignments were moved into the row expansions because
     * they can never be selected. Named in the heading, because a table that silently shows fewer
     * rows than the configuration holds is the same small lie this pane is built to avoid.
     */
    unreachableRowCount: number;

    /**
     * German count line of the block heading, e.g. "101 Zuordnungen · 190 Zeilen".
     *
     * Counts the CONFIGURATION, never the current view: it keeps naming every assignment the block
     * holds, and while the visibility switch hides some it says so in a further clause rather than
     * quietly counting down. A heading that shrank with the filter would make the hidden part
     * indistinguishable from a part that does not exist.
     */
    countLabel: string;

    /** Explains what {@link countLabel} counts and — when there is one — what the zero block adds. */
    countTooltip: string;

    /**
     * The active row filter of THIS block, or an empty string.
     *
     * VISIBILITY ONLY, like {@link CpProvisionAgentConfigurationComponent.zeroRowsExpanded}: it
     * decides which rows are drawn and never which rows exist, never their order and never a value.
     *
     * Held here rather than read back off the table because p-table nulls its own `filteredValue`
     * when the filter happens to match EVERY row — so `filteredValue` answers "is anything hidden",
     * not "is a filter active", and only the second question may drive the "X von Y" line and the
     * empty message.
     */
    filterTerm: string;

    /** Whether any visible row carries a Laufzeit band; false drops the column entirely. */
    hasTermBand: boolean;

    /** Whether any visible row carries an Eintrittsalter band. Today none does. */
    hasEntryAgeBand: boolean;

    /** Whether any visible row or assignment names a Haftungsart. */
    hasLiability: boolean;

    /** Column count, for the `colspan` of the expanded detail row. */
    columnCount: number;
}

/**
 * navItem "Provisionskonfiguration" of the ProvisionAgent OBC — the Beratersicht on the resolved
 * provision configuration of ONE Abrechnungskonto.
 *
 * ── THE QUESTION THIS PANE ANSWERS
 *
 * "Was bekomme ich für welches Geschäft — und warum?" The "warum" is the origin: a factor without
 * the matrix it came from is worthless here, because 127 of the 128 matrices of the reference stock
 * inherit. Every displayed number therefore carries its origin chip, and the full K-7 chain sits one
 * click away in the row expansion.
 *
 * ── WHAT IS NOT DONE HERE, AND WHY IT MATTERS
 *
 * NOTHING IS COMPUTED (requirement §7.6/§7.8, capability-matrix B1/F1). Not a factor, not a band,
 * not a rank, not an origin — every number on this page arrives finished from the endpoint, and
 * re-deriving one here would put a second copy of the resolution beside the one the provision run
 * pays out from.
 *
 * ── ORDER: DELIVERED BY DEFAULT, RE-ORDERABLE ON DEMAND (client order of 02.09.2026)
 *
 * The endpoint delivers the components in `orderPriority` order and the assignments in the order
 * the engine evaluates them; reading the table top to bottom IS reading the decision procedure, and
 * the unconditional assignment (no condition set, `fallback`) normally stands last, labelled
 * "Alle Verträge (unbedingt)". That order is the DEFAULT and
 * is restored by one click. What was added on the client's explicit order is a sort of the DISPLAY
 * MODEL — never of `component.assignments`, never of a value — under four conditions that keep the
 * explanation intact:
 *
 *   1. the sort works on ASSIGNMENT level: bands and comparison lines travel with their lead line,
 *      because a per-line sort would tear apart rows that only mean something together;
 *   2. the key is the GROUP EXTREMUM over the assignment's own lines (ascending the minimum,
 *      descending the maximum), NOT the lead line's value — the lead line is the first DELIVERED
 *      line, and 8.414 of 10.443 measured lines are inherited, so its value is not the assignment's
 *      highest or lowest anything;
 *   3. the deviation is VISIBLE — a chip, a changed introductory sentence and a "Prüfreihenfolge"
 *      button on every table that is sorted;
 *   4. a key-date change and a comparison switch reset it, with the reason named on screen: after
 *      either, the previous order was a statement about other rows.
 *
 * The other freedom of this component remains VISIBILITY (which columns exist, which zero rows are
 * collapsed, which rows the filter hides) — never content.
 *
 * The column set is DATA: {@link ProvisionAgentConfigurationComponentDto.occupiedDimensions}
 * decides it. Of thirteen assignment dimensions only four are occupied in the reference stock, and
 * another tenant occupies others; a hard-wired column list is the documented mistake here
 * (requirement §2.1 b).
 *
 * ── THE EMPTY ANSWER IS THE COMMON CASE
 *
 * 320 of 355 accounts of the reference stock hold no group under the current Provisionsart and would
 * produce a blank page. `state = NO_GROUP_AT_KEY_DATE` is therefore rendered as an EXPLANATION built
 * from `otherProvisionTypeGroups` — which Provisionsart the adviser IS in, and since when — on the
 * client's explicit "Erklärung statt Leere" decision (capability-matrix H9).
 *
 * ── THE KEY DATE IS THE AXIS OF THE WHOLE PANE
 *
 * The configuration is evaluated at CONTRACT START, never at run date
 * (`ProvisionAssignmentMatchingService`: the provision is earned when the contract is closed). The
 * picker therefore reads "Konfiguration für Verträge mit Beginn am …" and defaults to today; looking
 * back is the normal use, not an edge case.
 *
 * ── SHAPE
 *
 * Follows the sibling shared components of this OBC ({@link CpProvisionAgentReserveCoverageComponent}
 * for the `@Input() provisionAgentId` / refresh shape, {@link CpConfigCheckPanelComponent} for the
 * busy/error/empty states of a custom-endpoint panel). It is deliberately NOT a widget: the resolved
 * rows are not an entity — the inheritance is flattened and the origin is derived — and the valid
 * `dataSource`/`dataProvider` combinations all presuppose an entity query (capability-matrix H3).
 *
 * The detail mechanism is PrimeNG ROW EXPANSION (design gate FD-4). Not `mvs-review-workbench` —
 * its premise, a worklist queue, does not exist here — and emphatically not a hand-built
 * `p-splitter`, which the gate excludes as HAND-ROLLED.
 *
 * ── THE STICKY HEAD IS INERT ON ONE ENTRY PATH — KNOWN, ACCEPTED, DO NOT "FIX"
 *
 * `.cp-provision-agent-config-head` sticks against `.cp-provision-agent-full`, the scroll area the
 * OBC owns (see that component's SCSS). That works on the path this pane is actually opened from:
 * the cp dashboard workbench, where the pane is height-bound and scrolls itself — measured
 * 2026-09-02, the head holds its position at y=213,5 while the pane scrolls 1.500 px, and the page
 * is 8.574 px long there, which is why the client asked for it.
 *
 * On the OTHER path — opening the agent as the MAIN object, URL form
 * `/#/?tid=cp.ProvisionAgent:<id>:object&tid-action=3&tid-mode=full&navItem=configuration` — the same
 * head does nothing: `.cp-provision-agent-full` is not height-bound there, so `clientHeight` equals
 * `scrollHeight` (measured 69.037) and the element never scrolls; the DOCUMENT scrolls instead, and
 * a sticky child of a scrollport that never moves cannot stick. Measured on that path: 119 → −1.881.
 *
 * That is ACCEPTED, on the client's decision of 02.09.2026, for one reason: inside the cp module no
 * click leads to that path — `navigateTo(…, 'main')` is used for configuration objects only, never
 * for `cp.ProvisionAgent` — it is reachable by direct link or global search alone. Making it work
 * there requires lifting the head OUT of this component into the full view's template, with an
 * `@Output` context object; that design was weighed and rejected as heavier than what was ordered.
 * Whoever finds the head not sticking on that URL has found a documented gap, not a defect.
 */
@Component({
    selector: 'cp-provision-agent-configuration',
    templateUrl: './cp-provision-agent-configuration.component.html',
    styleUrls: ['./cp-provision-agent-configuration.component.scss'],
    standalone: false
})
export class CpProvisionAgentConfigurationComponent implements OnInit, OnChanges, OnDestroy {

    /** Id of the Abrechnungskonto (`cp.ProvisionAgent`) whose configuration is shown. */
    @Input() provisionAgentId: number;

    busy: boolean;
    initialized: boolean;

    /**
     * German headline of a failed load; null while things are fine.
     *
     * ── WHY THIS IS NOT ONE GENERIC SENTENCE
     *
     * A failed call that renders nothing is INDISTINGUISHABLE from a valid empty answer, and this
     * pane has a legitimately empty answer for 320 of 355 accounts — so "nothing on screen" is the
     * one signal that must never be ambiguous here. Measured: `GET …/9000003052/provisionConfiguration`
     * answers 404 with an EMPTY BODY, because that id is an `am.Agent` master record and not a
     * Provisionskonto. Without the status, the reader cannot tell "dieser Berater hat keine
     * Konfiguration" from "der Aufruf ist fehlgeschlagen" from "die Seite ist kaputt".
     */
    loadError: string;

    /** Explanatory second line of a failed load — what it means and what can be done. */
    loadErrorHint: string;

    /** HTTP status of the failed load, shown verbatim so a report from the field is actionable. */
    loadErrorStatus: number;

    /** The endpoint's answer, unmodified. */
    configuration: ProvisionAgentConfigurationDto;

    /**
     * The assembled component blocks — the render model of the whole pane, rebuilt on every load.
     * Empty whenever {@link configuration} carries no resolved components.
     */
    blocks: CpConfigurationBlock[] = [];

    /**
     * The chosen key date, bound to the `p-datePicker`. Defaults to TODAY — but the whole point of
     * the control is looking BACK: the applicable configuration is the one that was valid at the
     * contract's start.
     */
    keyDate: Date = new Date();

    /**
     * Whether the rows of the other Vermittlergruppen come along. Default OFF (client decision) and
     * a server round trip when switched, because the server guards that branch with a second
     * `checkAccess` on the Provisionsart (FD-5) — it is not a client-side filter.
     */
    includeGroupComparison: boolean = false;

    /**
     * Whether the assignments for which THIS adviser has no rate of his own are hidden.
     *
     * ── WHY IT EXISTS
     *
     * Measured on clone/rathje: 2.930 of 6.300 assignments carry no table row at all — they appear
     * as "kein eigener Satz" — and in the account of the client's screenshot three of the four
     * component tables consist of nothing else. Sorting such a table sorts holes. The switch answers
     * the question the pane is opened with, "was bekomme ich", by leaving out the combinations for
     * which the answer is "nichts".
     *
     * ── DEFAULT OFF, AND THAT IS BINDING
     *
     * Switched on, the pane shows LESS than the configuration holds. A view that quietly reduces
     * itself is read as missing data, not as a filter — so it starts off and the reader turns it on.
     *
     * ── VISIBILITY ONLY
     *
     * Exactly the same limit as {@link zeroRowsExpanded} and {@link CpConfigurationBlock.filterTerm}:
     * it decides which rows are DRAWN. It changes no order (the visible rows keep their delivery
     * order, and an active sort is simply re-applied to the smaller set), no value, and none of the
     * counts that describe the configuration. It needs no server round trip — unlike the comparison
     * switch, which is a request parameter.
     *
     * A whole assignment is hidden, not just its empty line: the line carries the dimension cells,
     * so hiding it alone would leave any comparison rows of that assignment without a heading. With
     * the group comparison on, the compared groups' values for that assignment therefore disappear
     * along with it — which is what "Zuordnung ausblenden" says, and one click brings it back.
     */
    hideAssignmentsWithoutOwnRow: boolean = false;

    /**
     * The component sections the visibility switch currently swallows whole, by name.
     *
     * Rendered as a sentence above the first block. A section that disappears without being named
     * would be indistinguishable from a Provisionskomponente that does not exist — the same small
     * lie the whole pane is built to avoid.
     */
    hiddenBlockNames: string[] = [];

    /**
     * Per component id: whether the assignments without any remuneration are unfolded.
     * VISIBILITY ONLY — the rows are always loaded and always counted.
     */
    zeroRowsExpanded: { [componentId: string]: boolean } = {};

    /**
     * The field the row filter searches, as ONE stable array instance.
     *
     * Deliberately not the literal `[globalFilterFields]="['searchText']"`: an array literal in a
     * template is rebuilt on every change-detection pass, so the input would look changed forever.
     * The same reasoning that keeps {@link blocks} out of the template applies to this one word.
     */
    readonly rowFilterFields: string[] = ['searchText'];

    /**
     * German sentence naming WHY a sort disappeared, or null.
     *
     * A key-date change and a comparison switch both reload and rebuild every block, which drops the
     * sort. Silently: the chip vanishes, the table jumps back into evaluation order and the reader
     * is left to wonder. The sentence says which of the two happened and why the old order could not
     * be kept. Cleared by the next sort, the next reset and the next load.
     */
    sortResetNotice: string;

    /**
     * Every `p-table` currently rendered by this component.
     *
     * Needed for exactly one thing: clearing PrimeNG's own sort state before the blocks are rebuilt.
     * Note what a reload does to the tables: `@if (busy)` in the template tears the whole block
     * section down while the request is in flight, so the table INSTANCES do NOT survive it — the
     * `@for` tracking by `componentId` only keeps instances within one rendered section. PrimeNG's
     * `sortField` therefore dies with the instance; this reset covers the synchronous window before
     * `busy` flips and any path that rebuilds `blocks` without a busy cycle. A table that kept its
     * `sortField` would re-run `sortSingle()` the moment a new `value` arrives (Table.onChanges) —
     * silently re-applying a sort over data fetched for another key date.
     */
    @ViewChildren(Table) tables: QueryList<Table>;

    protected objectChangeSubscription: Subscription;

    constructor(
        protected configurationService: CpProvisionAgentConfigurationService,
        protected mvsObjectService: MvsObjectService,
        protected navigationService: MvsObjectNavigationService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.refreshComponent();
    }

    /**
     * Reloads when the agent's group membership changes.
     *
     * `cp.ProvisionAgentGroup` is the ONE entity that can turn this pane's whole answer over
     * without anything on the pane being touched: it decides which Vermittlergruppe — and hence
     * which matrix column — the resolution reads. Matrix and assignment changes are configuration
     * work done on the config page, not here, and arrive on the next open.
     */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionAgentGroup') {
                this.refreshComponent();
            }
        });
    }

    /** Loads the configuration for the current agent, key date and comparison setting. */
    refreshComponent(): void {

        // PrimeNG's sort state lives on the TABLE, not on the block. The `@if (busy)` below destroys
        // the tables for the duration of the reload, so the state normally dies with them; the reset
        // is the belt for the window before `busy` flips (see the `tables` comment).
        this.clearTableSortState();
        this.sortResetNotice = null;

        if (!this.provisionAgentId) {
            this.configuration = null;
            this.blocks = [];
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.loadError = null;
        this.loadErrorHint = null;
        this.loadErrorStatus = null;

        this.configurationService.getProvisionConfiguration(
            this.provisionAgentId,
            null,
            this.formatKeyDate(this.keyDate),
            this.includeGroupComparison
        ).subscribe({
            next: (configuration: ProvisionAgentConfigurationDto) => {
                this.configuration = configuration;
                this.buildBlocks();
                this.busy = false;
                this.initialized = true;
            },
            error: (error: HttpErrorResponse) => {
                // No partial answer is shown: a half-resolved configuration read as a full one is
                // worse than a visible failure on a screen that states what somebody earns. And the
                // failure REPLACES the page rather than sitting beneath it — a Steckbrief rendered
                // from a null answer reads as "dieser Berater hat eben nichts", which is exactly the
                // silent failure this branch exists to prevent.
                this.configuration = null;
                this.blocks = [];
                this.applyLoadError(error);
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    /**
     * The date picker changed — the key date is a server parameter, so this reloads.
     *
     * The reload rebuilds every block and therefore drops any sort. That is deliberate and it is
     * SAID: the rows the sort ordered are gone, so keeping the order would be an assertion about
     * data that is no longer on screen.
     */
    onKeyDateChange(): void {

        if (!this.keyDate) {
            // An emptied picker means "heute" again; the endpoint defaults the same way.
            this.keyDate = new Date();
        }

        const wasSorted: boolean = this.isAnySortActive();

        this.refreshComponent();

        this.sortResetNotice = wasSorted
            ? 'Der Stichtag wurde gewechselt — die Tabellen stehen wieder in Prüfreihenfolge. '
                + 'Die vorherige Sortierung galt für die Konfiguration eines anderen Tages.'
            : null;
    }

    /**
     * The comparison switch changed — likewise a server parameter (FD-5), so this reloads.
     *
     * Resets the sort for the same reason as the key date, and says so: switching the comparison on
     * adds the other Vermittlergruppen's lines to every assignment (measured up to 47 lines on one
     * assignment), switching it off removes them again.
     */
    onComparisonChange(): void {

        const wasSorted: boolean = this.isAnySortActive();

        this.refreshComponent();

        this.sortResetNotice = wasSorted
            ? 'Der Gruppenvergleich wurde umgeschaltet — die Tabellen stehen wieder in '
                + 'Prüfreihenfolge. Die vorherige Sortierung galt für einen anderen Zeilenbestand.'
            : null;
    }

    /**
     * The visibility switch changed — NO reload, and no sort reset.
     *
     * ── WHY NO SERVER CALL
     *
     * The hidden assignments are already in the payload; hiding them is a decision about drawing,
     * not about data. Reloading would also throw away the sort and the row expansions for nothing.
     *
     * ── WHY NO SORT RESET, unlike the key date and the comparison switch
     *
     * Those two REPLACE the rows: after them, an order established over the previous set is a
     * statement about data that is gone. This switch removes rows without touching the order of the
     * ones that remain — the sort is simply re-applied to the smaller set and says exactly what it
     * said before. Resetting here would destroy a still-valid answer.
     */
    onHideWithoutOwnRowChange(): void {

        for (const block of this.blocks || []) {
            this.applyRowVisibility(block);
        }

        this.applyHiddenBlocks();

        // The tables' `filteredValue` was computed over the old arrays; re-running each table's own
        // global filter rebuilds it. Done per table with the term the table itself holds, so the
        // two tables of a block cannot borrow each other's filter.
        this.refilterTables();
    }

    /**
     * Rebuilds the visible rows of ONE block from the complete set, then re-applies whatever sort is
     * active on each of its two tables.
     *
     * The order of the three steps matters: derive the visible delivery order, put it into the bound
     * array, and only then sort — sorting a stale array would leave rows on screen that the filter
     * has just removed.
     */
    protected applyRowVisibility(block: CpConfigurationBlock): void {

        if (!block) {
            return;
        }

        block.payingRowsEvaluationOrder = this.visibleRows(block.payingRowsAll);
        block.zeroRowsEvaluationOrder = this.visibleRows(block.zeroRowsAll);

        block.payingRows.splice(0, block.payingRows.length, ...block.payingRowsEvaluationOrder);
        block.zeroRows.splice(0, block.zeroRows.length, ...block.zeroRowsEvaluationOrder);

        if (block.payingSort) {
            this.reorderByAssignment(block.payingRowsEvaluationOrder, block.payingRows, block,
                block.payingSort.field, block.payingSort.order);
        }

        if (block.zeroSort) {
            this.reorderByAssignment(block.zeroRowsEvaluationOrder, block.zeroRows, block,
                block.zeroSort.field, block.zeroSort.order);
        }

        this.applyHiddenCounts(block);
    }

    /**
     * The rows of one table that are currently drawn — everything, or everything except the
     * assignments this adviser has no own rate for.
     *
     * Grouped by assignment before deciding, because the decision belongs to the ASSIGNMENT: an
     * assignment counts as "ohne eigenen Satz" when NONE of its own (non-comparison) lines carries a
     * resolved matrix row. Delivery order is preserved by construction — the input is walked once and
     * the survivors are kept in place.
     */
    protected visibleRows(
        allRows: ReadonlyArray<CpConfigurationDisplayRow>): ReadonlyArray<CpConfigurationDisplayRow> {

        if (!this.hideAssignmentsWithoutOwnRow) {
            return allRows;
        }

        const assignmentHasOwnRow: Map<string, boolean> = new Map();

        for (const displayRow of allRows) {
            const key: string = this.assignmentKey(displayRow);
            if (!assignmentHasOwnRow.get(key)) {
                assignmentHasOwnRow.set(key, !displayRow.comparisonGroupName && !!displayRow.row);
            }
        }

        return Object.freeze(
            allRows.filter(displayRow => assignmentHasOwnRow.get(this.assignmentKey(displayRow))));
    }

    /**
     * Counts what the visibility switch currently hides in ONE block, and writes the heading clause.
     *
     * The count comes from the difference between the complete and the visible set, so it can never
     * drift from what is actually on screen.
     */
    protected applyHiddenCounts(block: CpConfigurationBlock): void {

        block.hiddenPayingRowCount =
            block.payingRowsAll.length - block.payingRowsEvaluationOrder.length;
        block.hiddenPayingAssignmentCount = this.countAssignments(block.payingRowsAll)
            - this.countAssignments(block.payingRowsEvaluationOrder);

        const hiddenZeroRows: number =
            block.zeroRowsAll.length - block.zeroRowsEvaluationOrder.length;
        const hiddenZeroAssignments: number = this.countAssignments(block.zeroRowsAll)
            - this.countAssignments(block.zeroRowsEvaluationOrder);

        block.hiddenRowCount = block.hiddenPayingRowCount + hiddenZeroRows;
        block.hiddenAssignmentCount = block.hiddenPayingAssignmentCount + hiddenZeroAssignments;

        block.countLabel = this.buildCountLabel(
            block.payingAssignmentCount, block.payingRowCount, block.unreachableRowCount,
            block.hiddenAssignmentCount);
    }

    /** How many distinct assignments a set of display rows covers. */
    protected countAssignments(displayRows: ReadonlyArray<CpConfigurationDisplayRow>): number {

        const keys: Set<string> = new Set<string>();

        for (const displayRow of displayRows || []) {
            keys.add(this.assignmentKey(displayRow));
        }

        return keys.size;
    }

    /** The grouping key of a display row: its assignment, or the row itself when there is no id. */
    protected assignmentKey(displayRow: CpConfigurationDisplayRow): string {
        return displayRow?.assignment?.assignmentId != null
            ? 'a' + displayRow.assignment.assignmentId
            : 'k' + displayRow?.rowKey;
    }

    /**
     * Re-runs each rendered table's own global filter.
     *
     * Called after the bound arrays were re-spliced: `filteredValue` was computed over the previous
     * contents and would otherwise keep rows on screen that are no longer in `value`. Each table is
     * re-filtered with the term IT holds — the two tables of a block have separate filters, and only
     * the paying one ever has a term.
     */
    protected refilterTables(): void {

        this.tables?.forEach(table => {

            const globalFilter = table.filters ? table.filters['global'] : null;
            const term: string = globalFilter && !Array.isArray(globalFilter)
                ? (globalFilter.value || '')
                : '';

            table.filterGlobal(term, 'contains');
        });
    }

    /**
     * Opens ONE configuration record in the LEFT side panel — the assignment, the matrix row or one
     * step of the inheritance chain.
     *
     * ── WHY LEFT AND NOT "MAIN"
     *
     * Client decision of 02.09.2026: the annotation "Via navigation service links das objekt öffnen
     * lassen" is read as a POSITION. The module's own precedent (`CpConfigCheckPanelComponent`)
     * navigates to `'main'`, but from this pane that is the wrong mode: `'main'` replaces the whole
     * route (`app.main.component.html` renders the router outlet only while no object is current),
     * so opening a record from the dashboard would tear down the selection, the key date, the filter
     * and every open row expansion — from a pane whose purpose is "look it up and read on".
     *
     * ── CTRL/CMD-CLICK
     *
     * `handleObjectNavigation` reads the modifier off the event and opens `_blank`. It is called ONLY
     * in that branch: without a modifier the same method performs a full `_self` navigation, and
     * with a `params` object the undefined `params.openNewTab` would overwrite the detection.
     *
     * Inert without an id, which is the normal state of a chain chip while the backend does not yet
     * deliver the parallel id list.
     */
    openObject(objectType: string, objectId: number, event?: MouseEvent): void {

        if (!objectType || objectId == null) {
            return;
        }

        if (event && (event.ctrlKey || event.metaKey)) {
            this.navigationService.handleObjectNavigation(
                new ObjectIdentifier(objectType, objectId), event);
            return;
        }

        const navigationEntry: MvsObjectNavigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            objectType,
            objectId,
            'object', '', '', '',
            MvsObjectNavigationActionEnum.display,
            'side'
        );

        this.navigationService.navigateTo(navigationEntry, 'left');
    }

    /**
     * The currently requested key date as `YYYY-MM-DD`, for the template.
     *
     * The error card shows it alongside the requested id, because "which day did it ask for" is the
     * second half of reproducing a failed call from a report.
     */
    keyDateForDisplay(): string {
        return this.formatKeyDate(this.keyDate);
    }

    /**
     * Turns a failed call into a sentence a human can act on.
     *
     * Three cases are told apart because they need three different actions, and telling them apart
     * is the whole point of the branch — a single "konnte nicht geladen werden" would leave the
     * reader exactly where the blank page left them.
     */
    protected applyLoadError(error: HttpErrorResponse): void {

        this.loadErrorStatus = error?.status;

        if (error?.status === 404) {
            this.loadError = 'Zu diesem Objekt gibt es kein Abrechnungskonto.';
            // Measured on the running tenant: the id that produced this was an am.Agent master
            // record. That is by far the most likely cause and it is worth naming, because the
            // reader can check it in one click — but it is stated as a suspicion, not as a fact,
            // since a deleted or foreign account produces the same 404.
            this.loadErrorHint = 'Die geöffnete Id ist möglicherweise ein Vermittlerstammsatz '
                + '(am.Agent) und nicht das Provisionskonto (cp.ProvisionAgent). Beide sind '
                + 'verschiedene Objekte mit verschiedenen Ids. Möglich ist auch, dass das '
                + 'Abrechnungskonto gelöscht wurde.';
            return;
        }

        if (error?.status === 403) {
            this.loadError = 'Keine Berechtigung für die Provisionskonfiguration dieses Kontos.';
            this.loadErrorHint = 'Der Zugriff wurde vom Server abgelehnt. Die Auskunft verlangt ein '
                + 'Leserecht auf das Abrechnungskonto und auf die Provisionsart — bei '
                + 'eingeschaltetem Gruppenvergleich zusätzlich auf die verglichene Provisionsart. '
                + 'Ohne Vergleich noch einmal versuchen oder das Recht anfordern.';
            return;
        }

        if (!error?.status) {
            this.loadError = 'Der Server ist nicht erreichbar.';
            this.loadErrorHint = 'Die Anfrage kam nicht an — Netzwerk oder Backend nicht verfügbar. '
                + 'Erneut versuchen; bleibt es dabei, ist es kein Fehler dieser Seite.';
            return;
        }

        this.loadError = 'Technischer Fehler beim Laden der Provisionskonfiguration.';
        this.loadErrorHint = 'Der Server hat mit HTTP ' + error.status
            + ' geantwortet. Bitte mit der unten genannten Id melden.';
    }

    /**
     * `YYYY-MM-DD` from the LOCAL date parts.
     *
     * Deliberately not `toISOString()`: that converts to UTC first, so east of Greenwich a date
     * picked at midnight comes out as the previous day. On a pane whose entire answer hangs on the
     * key date, an off-by-one day is a wrong answer — periods start and end on day boundaries.
     */
    protected formatKeyDate(date: Date): string {

        if (!date) {
            return null;
        }

        const month: string = String(date.getMonth() + 1).padStart(2, '0');
        const day: string = String(date.getDate()).padStart(2, '0');

        return `${date.getFullYear()}-${month}-${day}`;
    }

    // =================================================================================
    // Display state — all of it VISIBILITY, none of it business content
    // =================================================================================

    /** True when the endpoint resolved a group and there is a configuration to render. */
    isResolved(): boolean {
        return this.configuration?.state === 'RESOLVED' || this.configuration?.state === 0;
    }

    /** True for the common case: the Provisionsart is known, the agent has no group on that day. */
    isNoGroupAtKeyDate(): boolean {
        return this.configuration?.state === 'NO_GROUP_AT_KEY_DATE' || this.configuration?.state === 1;
    }

    /** True when no Provisionsart could be determined at all. */
    isNoProvisionType(): boolean {
        return this.configuration?.state === 'NO_PROVISION_TYPE' || this.configuration?.state === 2;
    }

    /**
     * Assembles the render model of every component block — once per load, never from the template.
     *
     * ORDER IS TAKEN, NOT MADE: the components arrive in `orderPriority` order and the assignments
     * in the order the engine evaluates them. This method iterates both as delivered and contains no
     * comparator. It also FREEZES that delivery order per table
     * ({@link CpConfigurationBlock.payingRowsEvaluationOrder}), which is what the "Prüfreihenfolge"
     * button restores and what every sort starts from.
     */
    protected buildBlocks(): void {

        this.blocks = (this.configuration?.components || []).map(component => {

            const paying: ProvisionAgentConfigurationAssignmentDto[] = this.payingAssignments(component);
            const zero: ProvisionAgentConfigurationAssignmentDto[] = this.zeroAssignments(component);

            // The conditional columns are decided over the assignments that can actually appear —
            // the folded zero block included, because unfolding it must not change the table shape.
            const all: ProvisionAgentConfigurationAssignmentDto[] = component.assignments || [];

            // Built here and handed to the block as-is: the count line must state the number of
            // rows that are ACTUALLY drawn, and only the built array knows it. An assignment with
            // bands occupies several rows, so "Zuordnungen" and "Zeilen" are different numbers and
            // the heading names both.
            // ONE collector for the whole block, filled by both calls below: the universal
            // fallback is the same record whether it arrives under a paying or a zero assignment,
            // and the block shows it once either way.
            const universalFallbacks: Map<string, CpConfigurationUniversalFallback> = new Map();

            // Resolved BEFORE the rows, because every row's dimension cells are built against
            // exactly this array, in exactly this order — the header and the cells must walk the
            // same list or they drift apart.
            const dimensionColumns: ProvisionAgentConfigurationDimensionDto[] =
                this.dimensionColumns(component);

            const payingRows: CpConfigurationDisplayRow[] =
                this.buildDisplayRows(paying, dimensionColumns, universalFallbacks);
            const zeroRows: CpConfigurationDisplayRow[] =
                this.buildDisplayRows(zero, dimensionColumns, universalFallbacks);

            const universalFallbackRows: CpConfigurationUniversalFallback[] =
                Array.from(universalFallbacks.values());

            // Only now can the "kein eigener Satz" wording be settled: it cross-references the
            // fallback line, and whether the block HAS one is known only once every assignment of
            // the block has been walked.
            this.applyNoRateHints(payingRows, universalFallbackRows.length > 0);
            this.applyNoRateHints(zeroRows, universalFallbackRows.length > 0);

            // Counted from the built rows, not from the payload: only the built rows know how many
            // candidates were moved into the expansions, and the heading must describe the table
            // that is actually on screen.
            const unreachableRowCount: number = this.countUnreachableCandidates(payingRows);

            const block: CpConfigurationBlock = {
                component: component,
                dimensionColumns: dimensionColumns,
                dimensionSortFields: dimensionColumns.map(
                    column => DIMENSION_SORT_FIELD_PREFIX + column.key),
                sortColumnLabels: null,
                rowLevelSortFields: null,
                payingRows: payingRows,
                // The frozen copies are taken HERE, from the freshly built arrays, before anything
                // can have re-ordered them. `Object.freeze` on the array, not on its rows: the rows
                // are the very objects the table renders and still receive their `noRateHint` below.
                payingRowsAll: Object.freeze(payingRows.slice()),
                payingRowsEvaluationOrder: Object.freeze(payingRows.slice()),
                zeroRows: zeroRows,
                zeroRowsAll: Object.freeze(zeroRows.slice()),
                zeroRowsEvaluationOrder: Object.freeze(zeroRows.slice()),
                hiddenAssignmentCount: 0,
                hiddenRowCount: 0,
                hiddenPayingAssignmentCount: 0,
                hiddenPayingRowCount: 0,
                hiddenBlock: false,
                payingSort: null,
                zeroSort: null,
                zeroAssignmentCount: zero.length,
                // Read from the map, so an unfolded zero block survives a key-date reload.
                zeroBlockExpanded: this.isZeroBlockExpanded(component),
                zeroToggleLabelShow: zero.length + ' Kombinationen ohne Vergütung anzeigen',
                zeroToggleLabelHide: zero.length + ' Kombinationen ohne Vergütung ausblenden',
                universalFallbackRows: universalFallbackRows,
                payingAssignmentCount: paying.length,
                payingRowCount: payingRows.length,
                unreachableRowCount: unreachableRowCount,
                countLabel: this.buildCountLabel(paying.length, payingRows.length, unreachableRowCount, 0),
                countTooltip: this.buildCountTooltip(
                    zero.length, unreachableRowCount, universalFallbackRows.length),
                filterTerm: '',
                hasTermBand: this.hasTermBand(all),
                hasEntryAgeBand: this.hasEntryAgeBand(all),
                hasLiability: this.hasLiability(all),
                columnCount: 0
            };

            block.columnCount = 1 + block.dimensionColumns.length + 3
                + (block.hasTermBand ? 1 : 0)
                + (block.hasEntryAgeBand ? 1 : 0)
                + (block.hasLiability ? 1 : 0);

            this.applySortColumnMetadata(block);

            // The visibility switch is a property of the COMPONENT and survives a reload, so a
            // freshly built block has to be brought into line with it immediately — otherwise a
            // key-date change would silently unhide what the reader had hidden.
            this.applyRowVisibility(block);

            return block;
        });

        this.applyHiddenBlocks();
    }

    /**
     * Decides which component sections the visibility switch swallows entirely, and NAMES them.
     *
     * ── WHY A SECOND LEVEL AT ALL
     *
     * Measured on clone/rathje over six resolved accounts (591 rows in the paying tables, 537 in the
     * folded zero blocks): **not one** "kein eigener Satz" line stands in a paying table — every one
     * of them is a zero assignment and therefore already folded away. What the reader actually sees
     * for a component this adviser earns nothing from is a full section — heading, two sentences of
     * prose, an empty table, the "alles Übrige" box and a toggle — that says nothing. In the account
     * of the client's screenshot that is three of the four sections. Hiding the assignments without
     * hiding those sections would leave the switch without visible effect, which is the same rule one
     * level up, not a different one.
     *
     * ── WHAT IS NOT HIDDEN
     *
     * A section whose catch-all rate ("alles Übrige") is non-zero stays, whatever its table shows:
     * that line IS a payment. This is the only place where the switch could hide MONEY, and on the
     * reference tenant it never fires — every catch-all there is 0 — so it was proved rather than
     * asserted. Probe on the running component (account 9000001068, section "Zusatz
     * Neukundenaktion", switch on): with the catch-all at 0 the section is hidden (3 sections drawn);
     * with the same section's catch-all set to 2,5 it is drawn again (4 sections) although its table
     * is still empty and all 71 of its assignments are hidden; set back to 0, hidden again. Screenshot
     * `e2e/.playwright-mcp/cp-guard-nonzero-fallback.png`.
     */
    protected applyHiddenBlocks(): void {

        for (const block of this.blocks || []) {

            block.hiddenBlock = this.hideAssignmentsWithoutOwnRow
                && !block.payingRows.length
                && !(block.universalFallbackRows || []).some(
                    fallback => fallback.row?.factor != null && Number(fallback.row.factor) !== 0);
        }

        this.hiddenBlockNames = (this.blocks || [])
            .filter(block => block.hiddenBlock)
            .map(block => block.component.componentName)
            .filter(name => !!name);
    }

    /**
     * Writes the two sort lookup tables of ONE block: the German label per sort field, and whether
     * that field is a ROW property.
     *
     * Both are read only when a sort is applied — once per click, not per row — but they are built
     * here so that no string is assembled in a binding and so that the dimension labels come from
     * the SAME array the header walks.
     */
    protected applySortColumnMetadata(block: CpConfigurationBlock): void {

        const labels: { [field: string]: string } = {};
        const rowLevel: { [field: string]: boolean } = {};

        block.dimensionColumns.forEach((column, index) => {
            labels[block.dimensionSortFields[index]] = column.label || column.key;
            // A dimension belongs to the ASSIGNMENT: every line of a group carries the same value,
            // so "smallest / largest of the group" would be a meaningless thing to print.
            rowLevel[block.dimensionSortFields[index]] = false;
        });

        labels[SORT_FIELD_FACTOR] = 'Faktor';
        labels[SORT_FIELD_TERM] = 'Laufzeit';
        labels[SORT_FIELD_ENTRY_AGE] = 'Eintrittsalter';
        labels[SORT_FIELD_LIABILITY] = 'Haftung';
        labels[SORT_FIELD_AXIS] = 'Achse';
        labels[SORT_FIELD_ORIGIN] = 'Herkunft des Werts';

        rowLevel[SORT_FIELD_FACTOR] = true;
        rowLevel[SORT_FIELD_TERM] = true;
        rowLevel[SORT_FIELD_ENTRY_AGE] = true;
        rowLevel[SORT_FIELD_LIABILITY] = true;
        rowLevel[SORT_FIELD_AXIS] = true;
        rowLevel[SORT_FIELD_ORIGIN] = true;

        block.sortColumnLabels = labels;
        block.rowLevelSortFields = rowLevel;
    }

    /**
     * The dimension columns of ONE component block, in the order the backend listed them.
     *
     * Component-level, not page-level: a component that never uses "Sparte" does not carry that
     * column even when a sibling does. That is the single largest readability gain of this pane and
     * it costs nothing — it is data, not code.
     */
    protected dimensionColumns(
        component: ProvisionAgentConfigurationComponentDto): ProvisionAgentConfigurationDimensionDto[] {
        return component?.occupiedDimensions || [];
    }

    /** The value of one dimension on one assignment, or an empty string when it is not occupied. */
    dimensionValue(assignment: ProvisionAgentConfigurationAssignmentDto, key: string): string {

        const dimension: ProvisionAgentConfigurationDimensionDto =
            (assignment?.dimensions || []).find(candidate => candidate.key === key);

        return dimension?.value || '';
    }

    /**
     * The assignments that carry at least one remunerating row — the ones shown by default.
     *
     * THE COLLAPSE RULE, and why it is drawn at the ASSIGNMENT and not at the row: the reference
     * tenant builds "there is nothing for this combination" as an explicit assignment whose rows all
     * carry factor 0. Those drown the real ones and are folded away. An assignment with a MIX keeps
     * every one of its rows visible — hiding a zero band inside a banded assignment would hide a
     * real statement about one specific Laufzeit range, which is content, not noise.
     */
    protected payingAssignments(
        component: ProvisionAgentConfigurationComponentDto): ProvisionAgentConfigurationAssignmentDto[] {
        return (component?.assignments || []).filter(assignment => !this.isZeroAssignment(assignment));
    }

    /** The assignments folded away behind "N Kombinationen ohne Vergütung anzeigen". */
    protected zeroAssignments(
        component: ProvisionAgentConfigurationComponentDto): ProvisionAgentConfigurationAssignmentDto[] {
        return (component?.assignments || []).filter(assignment => this.isZeroAssignment(assignment));
    }

    /** An assignment that has rows and whose every row carries factor 0. */
    protected isZeroAssignment(assignment: ProvisionAgentConfigurationAssignmentDto): boolean {

        const rows: ProvisionAgentConfigurationRowDto[] = assignment?.rows || [];

        if (!rows.length) {
            return false;
        }

        return rows.every(row => row.factor != null && Number(row.factor) === 0);
    }

    /** Whether the zero block of one component is unfolded. */
    isZeroBlockExpanded(component: ProvisionAgentConfigurationComponentDto): boolean {
        return !!this.zeroRowsExpanded[String(component?.componentId)];
    }

    /**
     * Unfolds / folds the zero block of one component.
     *
     * Writes BOTH the persistent map — which outlives the block and keeps the fold across a reload
     * — and the block's own boolean, which is what the template reads. Two writes in a click
     * handler cost nothing; the alternative was a method call in three bindings per block.
     */
    toggleZeroBlock(block: CpConfigurationBlock): void {

        const key: string = String(block?.component?.componentId);
        const expanded: boolean = !this.zeroRowsExpanded[key];

        this.zeroRowsExpanded[key] = expanded;
        block.zeroBlockExpanded = expanded;
    }

    /**
     * The sentence a continuation line shows instead of repeating the dimensions.
     *
     * It names the ASSIGNMENT rather than just saying "see above", because the two lines can end up
     * separated by a sort or a filter — and then "above" is a lie.
     */
    protected buildContinuationHint(assignment: ProvisionAgentConfigurationAssignmentDto,
                                    bandCount: number): string {

        // Values may themselves read "A oder B" (several INCLUDE conditions) — joined as given,
        // never split. An assignment without a condition set is named as such.
        const dimensions = assignment.fallback
            ? 'Alle Verträge, ohne Bedingungssatz'
            : (assignment.dimensions || [])
                .map(dimension => dimension.value)
                .filter(value => !!value)
                .join(' / ');

        return 'Weiteres Laufzeitband derselben Zuordnung'
            + (dimensions ? ' (' + dimensions + ')' : '')
            + '. Die Zuordnung belegt ' + bandCount + ' Zeilen, weil ihre Matrix mehrere Bänder '
            + 'anbietet; die Merkmale stehen einmal auf der ersten. Welches Band am Ende zählt, '
            + 'entscheidet die Laufzeit des konkreten Vertrags.';
    }

    /**
     * Builds the display rows of one component block from a list of assignments.
     *
     * ORDER IS TAKEN, NOT MADE. The assignments arrive in evaluation order and are iterated as
     * delivered; within an assignment the resolved rows are iterated as delivered. Nothing here
     * sorts, and nothing may be added that does.
     *
     * The one thing it DOES decide is which candidates become table rows. Two kinds are lifted out,
     * both on the BACKEND's flag alone and both RELOCATED rather than dropped:
     *
     *  - {@link ProvisionAgentConfigurationRowDto.universalFallback} — the one rate every
     *    assignment inherits from the root matrix. Collected into `universalFallbacks` and shown
     *    ONCE at the bottom of the block. Checked FIRST: a record that is a single statement about
     *    the whole block must not also appear as a per-assignment candidate.
     *  - {@link ProvisionAgentConfigurationRowDto.shadowedByEarlierRow} — a candidate the engine
     *    can never reach. Moved to {@link CpConfigurationDisplayRow.unreachableCandidates} and
     *    rendered in the expansion instead. That is a VISIBILITY decision of the same family as the zero-block fold —
     * the row is relocated, never dropped — and it is taken on the BACKEND's flag alone. The rule
     * is emphatically not re-derived here: a candidate behind a BANDED row is the real catch-all
     * and must stay in the table, and only the backend knows which case a row is in.
     *
     * When the comparison is switched on, the other groups' rows for the same assignment are
     * appended DIRECTLY BENEATH the agent's own — same Faktor column, so the comparison is read
     * across one line rather than across two tables.
     */
    protected buildDisplayRows(
        assignments: ProvisionAgentConfigurationAssignmentDto[],
        dimensionColumns: ProvisionAgentConfigurationDimensionDto[],
        universalFallbacks: Map<string, CpConfigurationUniversalFallback>): CpConfigurationDisplayRow[] {

        const displayRows: CpConfigurationDisplayRow[] = [];

        for (const assignment of assignments || []) {

            const rows: ProvisionAgentConfigurationRowDto[] = assignment.rows || [];

            // The dimension cells belong to the ASSIGNMENT, not to one of its lines, so they are
            // resolved once here and every display row of this assignment shares the instance.
            const dimensionCells: string[] = this.buildDimensionCells(assignment, dimensionColumns);

            // So does the inheritance chain. Only WHICH step is the active one is a property of the
            // row, and that is one number per row instead of a second array of chips.
            const pathSteps: ReadonlyArray<CpConfigurationPathStep> = this.buildPathSteps(assignment);

            // Three-way split, in this precedence: the universal fallback leaves the assignment
            // entirely (it is a statement about the block), then the unreachable candidates move
            // to the expansion, and what remains is the table. Each list is built ONCE per
            // assignment; the two that survive are shared by reference, never copied per row.
            for (const row of rows) {
                if (this.isUniversalFallbackRow(row)) {
                    this.collectUniversalFallback(universalFallbacks, row, null);
                }
            }

            const rowsShown: ProvisionAgentConfigurationRowDto[] = rows.filter(
                row => !this.isUniversalFallbackRow(row) && !this.isUnreachableRow(row));

            const rowsUnreachable: ProvisionAgentConfigurationRowDto[] = rows.filter(
                row => !this.isUniversalFallbackRow(row) && this.isUnreachableRow(row));

            if (!rowsShown.length) {
                // An assignment with no SHOWABLE row is a statement of its own ("kein Wert") and is
                // shown as a line, never silently dropped — the completeness claim F1-V. This also
                // catches the theoretical case of an assignment whose every candidate is flagged
                // unreachable: the assignment keeps its line, and the candidates keep their place
                // in the expansion, rather than the whole assignment vanishing from the pane.
                displayRows.push({
                    rowKey: 'a' + assignment.assignmentId + '-none',
                    assignment: assignment,
                    row: null,
                    leadRow: true,
                    assignmentRowCount: 0,
                    continuationHint: '',
                    searchText: this.buildSearchText(assignment, null),
                    unreachableCandidates: rowsUnreachable,
                    dimensionCells: dimensionCells,
                    termBandText: '',
                    entryAgeBandText: '',
                    agentAxis: false,
                    pathSteps: pathSteps,
                    // No row, no origin: nothing in the chain is marked as the active step. The
                    // chain itself is still shown — it explains where a value COULD come from.
                    activePathIndex: -1,
                    liabilityOriginTitle: this.liabilityOriginTitle(null),
                    liabilityOriginDetail: this.liabilityOriginDetail(null, assignment),
                    liabilityTypeMissing: false,
                    invalidAtKeyDate: false
                });
                continue;
            }

            rowsShown.forEach((row, index) => {
                displayRows.push({
                    rowKey: 'a' + assignment.assignmentId + '-r' + (row.matrixRowId ?? index),
                    assignment: assignment,
                    row: row,
                    leadRow: index === 0,
                    assignmentRowCount: rowsShown.length,
                    continuationHint: index === 0 ? '' : this.buildContinuationHint(assignment, rowsShown.length),
                    searchText: this.buildSearchText(assignment, row),
                    unreachableCandidates: rowsUnreachable,
                    dimensionCells: dimensionCells,
                    termBandText: this.termBandLabel(row),
                    entryAgeBandText: this.entryAgeBandLabel(row),
                    agentAxis: this.isAgentAxis(row),
                    pathSteps: pathSteps,
                    activePathIndex: this.resolveActivePathIndex(assignment, pathSteps, row),
                    liabilityOriginTitle: this.liabilityOriginTitle(row),
                    liabilityOriginDetail: this.liabilityOriginDetail(row, assignment),
                    liabilityTypeMissing: this.isLiabilityTypeMissing(row),
                    invalidAtKeyDate: row.validAtKeyDate === false
                });
            });

            if (!this.includeGroupComparison) {
                continue;
            }

            for (const comparison of assignment.groupComparison || []) {
                (comparison.rows || []).forEach((row, index) => {

                    // A compared group has its own universal fallback; it belongs at the bottom
                    // of the block beside the agent's own, not on 190 comparison lines.
                    if (this.isUniversalFallbackRow(row)) {
                        this.collectUniversalFallback(
                            universalFallbacks, row, comparison.provisionTypeGroupName);
                        return;
                    }

                    displayRows.push({
                        rowKey: 'a' + assignment.assignmentId + '-g' + comparison.provisionTypeGroupId
                            + '-r' + (row.matrixRowId ?? index),
                        assignment: assignment,
                        row: row,
                        leadRow: false,
                        assignmentRowCount: 0,
                        continuationHint: '',
                        comparisonGroupName: comparison.provisionTypeGroupName,
                        searchText: this.buildSearchText(assignment, row),
                        // A foreign group's line does not carry THIS group's dead candidates.
                        unreachableCandidates: NO_UNREACHABLE_CANDIDATES,
                        dimensionCells: dimensionCells,
                        termBandText: this.termBandLabel(row),
                        entryAgeBandText: this.entryAgeBandLabel(row),
                        agentAxis: this.isAgentAxis(row),
                        // Same chain — the assignment is the same — but the compared group's row
                        // has its OWN origin within it, and that is the step marked on its line.
                        pathSteps: pathSteps,
                        activePathIndex: this.resolveActivePathIndex(assignment, pathSteps, row),
                        liabilityOriginTitle: this.liabilityOriginTitle(row),
                        liabilityOriginDetail: this.liabilityOriginDetail(row, assignment),
                        liabilityTypeMissing: this.isLiabilityTypeMissing(row),
                        invalidAtKeyDate: row.validAtKeyDate === false
                    });
                });
            }
        }

        return displayRows;
    }

    /**
     * Whether a candidate can never be selected — the BACKEND's answer, read defensively.
     *
     * `undefined` is treated as "reachable", which is the safe direction in both time and space:
     * against a backend that does not yet send the flag the pane simply behaves as it did before,
     * showing every candidate, rather than hiding rows on the strength of a missing field.
     */
    protected isUnreachableRow(row: ProvisionAgentConfigurationRowDto): boolean {
        return row?.shadowedByEarlierRow === true;
    }

    /**
     * Writes the "no rate of its own" statement onto every line that has no row.
     *
     * Runs once per block after the rows are built. Two arrivals lead here — an assignment the
     * endpoint sent without rows, and one whose only row was the universal fallback — and both
     * mean the same thing to a reader, so both get the same sentence. When the block has a
     * fallback line the sentence points at it; without one it would be a dangling reference, so
     * the wording changes rather than promising something that is not on screen.
     */
    protected applyNoRateHints(displayRows: CpConfigurationDisplayRow[], hasFallbackLine: boolean): void {

        const hint: string = hasFallbackLine
            ? 'Für diese Kombination ist kein eigener Satz hinterlegt. Es gilt die Zeile '
                + '„alles Übrige“ am Ende dieses Abschnitts.'
            : 'Für diese Kombination ist kein eigener Satz hinterlegt.';

        for (const displayRow of displayRows || []) {
            if (!displayRow.row) {
                displayRow.noRateHint = hint;
            }
        }
    }

    /**
     * Resolves the dimension cell texts of ONE assignment, aligned with the block's columns.
     *
     * Walks the SAME `dimensionColumns` array the header walks, so index i of the result is always
     * the value belonging to column i — the alignment the template relies on. Still delegates to
     * {@link dimensionValue}, which stays the single definition of "what is this dimension's value
     * on this assignment"; the only change is that it now runs ONCE per cell instead of on every
     * change-detection pass.
     */
    protected buildDimensionCells(
        assignment: ProvisionAgentConfigurationAssignmentDto,
        dimensionColumns: ProvisionAgentConfigurationDimensionDto[]): string[] {

        const cells: string[] = [];

        for (const column of dimensionColumns || []) {
            cells.push(this.dimensionValue(assignment, column.key));
        }

        return cells;
    }

    // =================================================================================
    // Inheritance chain and Haftungsherkunft — the row expansion (client annotations 2.1 / 2.4 / 2.5)
    // =================================================================================

    /**
     * Turns the delivered chain of ONE assignment into renderable chips — once per assignment.
     *
     * The ids come from the index-parallel {@link
     * ProvisionAgentConfigurationAssignmentDto.matrixInheritancePathIds}, which the backend adds
     * ADDITIVELY: while it is absent, or shorter than the path, the affected steps simply have no id
     * and are rendered as plain chips instead of links. Nothing is inferred from a missing index.
     *
     * The FIRST step is the assigned matrix itself — verified in the backend service (the chain is
     * built from `matrix` and its name is written before any break can apply) and measured on all
     * 6.300 assignments of clone/rathje (`path[0] != matrixName` in 0 of them). That is why the
     * separate section "Zugeordnete Matrix" is gone: it said the same thing twice. What it said is
     * carried into the first chip's tooltip instead of being dropped.
     */
    protected buildPathSteps(
        assignment: ProvisionAgentConfigurationAssignmentDto): ReadonlyArray<CpConfigurationPathStep> {

        const names: string[] = assignment?.matrixInheritancePath || [];

        if (!names.length) {
            return NO_PATH_STEPS;
        }

        const ids: number[] = assignment?.matrixInheritancePathIds || [];

        return names.map((name, index) => ({
            name: name,
            matrixId: ids[index] != null ? ids[index] : undefined,
            tooltip: index === 0
                ? 'Die Matrix, die dieser Zuordnung zugewiesen ist. Von hier aus wird aufwärts geerbt.'
                : 'Übergeordnete Matrix — ihre Zeilen werden an die der Stufe darunter angehängt.'
        }));
    }

    /**
     * Which step of the chain the displayed value physically comes from, or -1.
     *
     * ── ID FIRST, NAME AS THE FALLBACK
     *
     * `cp_provision_type_matrix.name` carries no unique index (only `(provision_type_id, alias)`
     * does), so two matrices of one chain could in principle share a name and BOTH be marked. The id
     * comparison removes that possibility. It is however genuinely a precaution and not a repair:
     * measured over 10.443 rows and 130 matrices of clone/rathje there are 0 duplicate names, 0
     * chains with a duplicate name and 0 rows whose origin matches more than one step.
     *
     * The name comparison is therefore kept as the explicit fallback for the deployment window in
     * which the backend does not yet deliver the ids — the behaviour the pane had all along.
     */
    protected resolveActivePathIndex(
        assignment: ProvisionAgentConfigurationAssignmentDto,
        pathSteps: ReadonlyArray<CpConfigurationPathStep>,
        row: ProvisionAgentConfigurationRowDto): number {

        if (!row || !pathSteps?.length) {
            return -1;
        }

        if (row.originMatrixId != null) {
            const byId: number = pathSteps.findIndex(step => step.matrixId === row.originMatrixId);
            if (byId >= 0) {
                return byId;
            }
        }

        if (!row.originMatrixName) {
            return -1;
        }

        return pathSteps.findIndex(step => step.name === row.originMatrixName);
    }

    /**
     * The FIRST line of "Herkunft der Haftungsart" — one of exactly four German words.
     *
     * The client asked for three ("Entweder Matrix, Zuweisung oder generell"); the fourth is the
     * state the code has and the other three cannot express. Mapping, with the measured share on
     * clone/rathje (10.443 rows):
     *
     * <ul>
     *   <li><b>Matrix</b> — the overwrite on the matrix row (1.411) or on the matrix assignment (0).
     *       Both are "die Matrix hat es gesetzt"; the finer enum label stays as the second line.</li>
     *   <li><b>Zuweisung</b> — a SPECIFIC row of the Haftungszuordnung (K-07) won (3.601).</li>
     *   <li><b>generell</b> — the DEFAULT row of the Haftungszuordnung won, the one WITHOUT a
     *       condition set (5.431 — 60 % of all "Zuordnung" rows, today indistinguishable from the line
     *       above and the reason this annotation exists).</li>
     *   <li><b>keine Haftung</b> — no source at all (0 in the reference stock, but a real engine
     *       state: no liability period and no chargeback, message M-041).</li>
     * </ul>
     *
     * Until the backend delivers `liabilityAssignmentDefaultRow`, the third case is reported as
     * "Zuweisung" — coarser than the truth, never something else than the truth.
     */
    protected liabilityOriginTitle(row: ProvisionAgentConfigurationRowDto): string {

        if (!row || !this.hasLiabilitySource(row)) {
            return 'keine Haftung';
        }

        if (this.isMatrixLiabilitySource(row)) {
            return 'Matrix';
        }

        return row.liabilityAssignmentDefaultRow === true ? 'generell' : 'Zuweisung';
    }

    /** The SECOND line — what the word above means for this row. Never empty. */
    protected liabilityOriginDetail(
        row: ProvisionAgentConfigurationRowDto,
        assignment: ProvisionAgentConfigurationAssignmentDto): string {

        if (!row || !this.hasLiabilitySource(row)) {

            // An assignment-level overwrite without a resolved row still says something.
            if (assignment?.liabilityTypeOverwriteName) {
                return 'Für diese Zeile ist keine Haftungsart aufgelöst; an der Zuordnung steht die '
                    + 'Überschreibung „' + assignment.liabilityTypeOverwriteName + '“.';
            }

            return 'Kein Haftungszeitraum, keine Rückbelastung.';
        }

        if (this.isMatrixRowLiabilitySource(row)) {
            return (row.liabilitySourceInfo?.label || 'Matrixzeilen-Überschreibung')
                + ' — schlägt die Überschreibung an der Zuordnung und die Haftungszuordnung.';
        }

        if (this.isMatrixLiabilitySource(row)) {
            return (row.liabilitySourceInfo?.label || 'Matrix-Überschreibung')
                + ' — schlägt die Haftungszuordnung.';
        }

        if (row.liabilityAssignmentDefaultRow === true) {
            return 'Haftungszuordnung ohne Bedingungssatz (alle Verträge) — sie gilt, weil keine '
                + 'speziellere Zeile passt.';
        }

        if (row.liabilityAssignmentDefaultRow === false) {
            return 'Haftungszuordnung — eine Zeile, die auf dieses Geschäft eingeschränkt ist.';
        }

        // The flag is absent (older backend). Then it is NOT known which row of the liability
        // assignment won — and measured, 5.431 of the 9.032 cases are the general default row.
        // Claiming "restricted row" here would be wrong in 60 % of the cases; the statement
        // therefore says exactly what it knows and names the gap.
        return 'Die Haftungszuordnung hat entschieden. Ob es eine auf dieses Geschäft '
            + 'eingeschränkte Zeile war oder die Zeile ohne Bedingungssatz, meldet die Auskunft '
            + 'noch nicht.';
    }

    /** True when the source of the Haftungsart is known but the type itself cannot be named. */
    protected isLiabilityTypeMissing(row: ProvisionAgentConfigurationRowDto): boolean {
        return this.hasLiabilitySource(row) && !row?.liabilityTypeName;
    }

    /**
     * Whether the row names a source at all. `null`/`undefined` is the "keine Haftung" state — the
     * engine folds "no candidate matched" and "a candidate said no liability" into this one value
     * and emits one message (M-041) for both, so the pane does not split them either.
     */
    protected hasLiabilitySource(row: ProvisionAgentConfigurationRowDto): boolean {
        return row?.liabilitySource != null && row.liabilitySource !== '';
    }

    /** True for both matrix overwrites — the row's (K-09) and the assignment's (K-10). */
    protected isMatrixLiabilitySource(row: ProvisionAgentConfigurationRowDto): boolean {
        return this.isMatrixRowLiabilitySource(row)
            || this.isEnumValue(row?.liabilitySource,
                ProvisionLiabilitySource, ProvisionLiabilitySource.MATRIX_OVERWRITE);
    }

    /** True for the overwrite on the MATRIX ROW, which beats everything else. */
    protected isMatrixRowLiabilitySource(row: ProvisionAgentConfigurationRowDto): boolean {
        return this.isEnumValue(row?.liabilitySource,
            ProvisionLiabilitySource, ProvisionLiabilitySource.MATRIX_ROW_OVERWRITE);
    }

    /**
     * Whether a wire value names the given constant — in either wire form.
     *
     * The endpoint sends the ORDINAL beside an `…Info` block; a backend older than that sends the
     * enum NAME. Both forms are resolved through the FE enum mirror — the constant itself and its
     * reverse-mapped key — so no raw string literal and no ordinal literal is compared anywhere in
     * this component.
     */
    protected isEnumValue(value: string | number, enumObject: object, constant: number): boolean {
        return value === constant || value === (enumObject as Record<number, unknown>)[constant];
    }

    /**
     * Whether a row is THE universal "everything else" rate — the BACKEND's answer, read
     * defensively. `undefined` means "an ordinary row", so the pane behaves exactly as it does
     * today until the field is delivered.
     *
     * The rule behind the flag is deliberately NOT re-derived here. It is not "factor 0", not "no
     * band" and not "origin = INHERITED" — it is "comes from the root of the inheritance tree AND
     * carries no restriction", and only the backend knows the tree.
     */
    protected isUniversalFallbackRow(row: ProvisionAgentConfigurationRowDto): boolean {
        return row?.universalFallback === true;
    }

    /**
     * Collects ONE universal fallback into the block's deduplicating map.
     *
     * The same physical record arrives under every assignment — measured 190 times — so the map is
     * keyed by matrix row AND group: the agent's own line and each compared group's line survive
     * side by side, while the 190 repetitions of one record collapse into one entry. Rows without
     * a `matrixRowId` fall back to a key built from their content, so a payload without ids still
     * deduplicates instead of producing one line per assignment.
     */
    protected collectUniversalFallback(
        target: Map<string, CpConfigurationUniversalFallback>,
        row: ProvisionAgentConfigurationRowDto,
        comparisonGroupName: string): void {

        if (!row) {
            return;
        }

        const identity: string = row.matrixRowId != null
            ? 'r' + row.matrixRowId
            : 'c' + row.factor + '|' + row.calculationMode + '|' + row.originMatrixName;

        const key: string = (comparisonGroupName || '') + '#' + identity;

        if (target.has(key)) {
            return;
        }

        target.set(key, {
            row: row,
            comparisonGroupName: comparisonGroupName || undefined,
            // The assignment is deliberately not passed: this record is a statement about the whole
            // BLOCK, inherited by every assignment alike, so an overwrite on one of them says nothing
            // about it.
            liabilityOriginTitle: this.liabilityOriginTitle(row),
            liabilityOriginDetail: this.liabilityOriginDetail(row, null),
            liabilityTypeMissing: this.isLiabilityTypeMissing(row)
        });
    }

    /**
     * How many unreachable candidates a set of display rows carries in total.
     *
     * Summed over the LEAD rows only. Every display row of an assignment points at the SAME
     * candidate array, so counting all of them would multiply the number by the assignment's band
     * count; exactly one row per assignment is the lead row.
     */
    protected countUnreachableCandidates(displayRows: CpConfigurationDisplayRow[]): number {
        return (displayRows || []).reduce(
            (total, displayRow) =>
                displayRow.leadRow ? total + displayRow.unreachableCandidates.length : total,
            0);
    }

    /**
     * The searchable text of ONE display row — built once, at build time.
     *
     * Holds what a reader would actually type when hunting for their case: the assignment's
     * dimension values (Gesellschaft, Vertragsart, Herkunft, Sparte …), the assigned matrix, the
     * matrix the value really came from, and the factor. "Alle Verträge (unbedingt) ohne
     * Bedingungssatz" is included for the fallback assignment (no condition set) because that is
     * the text the row VISIBLY carries — a filter that cannot find a row the table plainly labels
     * would be its own small lie.
     *
     * Nothing here is displayed and nothing here re-orders; this string exists only to decide
     * whether a row is drawn.
     */
    protected buildSearchText(
        assignment: ProvisionAgentConfigurationAssignmentDto,
        row: ProvisionAgentConfigurationRowDto): string {

        const parts: string[] = [];

        for (const dimension of assignment?.dimensions || []) {
            if (dimension?.value) {
                parts.push(dimension.value);
            }
        }

        if (assignment?.fallback) {
            parts.push('Alle Verträge (unbedingt) ohne Bedingungssatz');
        }
        if (assignment?.matrixName) {
            parts.push(assignment.matrixName);
        }
        if (row?.originMatrixName) {
            parts.push(row.originMatrixName);
        }
        if (row?.factor != null) {
            parts.push(String(row.factor));
        }

        return parts.join(' ');
    }

    /**
     * German count line of one block heading — "101 Zuordnungen · 190 Zeilen", correctly singular.
     *
     * Both numbers are named because they differ and the difference is the point: an assignment
     * with Laufzeitbändern occupies several lines, so "190 Zeilen" alone would read as 190 rules.
     */
    protected buildCountLabel(assignmentCount: number,
                              rowCount: number,
                              unreachableCount: number,
                              hiddenAssignmentCount: number): string {

        const assignments: string = assignmentCount === 1
            ? '1 Zuordnung'
            : assignmentCount + ' Zuordnungen';

        const rows: string = rowCount === 1
            ? '1 Zeile'
            : rowCount + ' Zeilen';

        let label: string = assignments + ' · ' + rows;

        if (unreachableCount) {
            // The relocated rows are NAMED, never merely omitted. "nicht erreichbar" rather than
            // "ausgeblendet": the reason they are gone is that the calculation cannot reach them, and
            // that is the fact worth carrying in four words.
            label += ' · ' + unreachableCount + ' nicht erreichbar';
        }

        if (hiddenAssignmentCount) {
            // The two numbers in front of this clause keep counting the CONFIGURATION. This one says
            // how much of it the current view leaves out, so the heading can never be read as "das
            // ist alles, was es gibt".
            label += ' · ' + hiddenAssignmentCount + ' ausgeblendet';
        }

        return label;
    }

    /** Says what the count line counts — and, above all, what it does NOT count. */
    protected buildCountTooltip(
        zeroAssignmentCount: number,
        unreachableCount: number,
        universalFallbackCount: number): string {

        let tooltip: string = 'Gezählt sind die Zuordnungen mit Vergütung und die Zeilen, die sie in '
            + 'dieser Tabelle belegen — eine Zuordnung belegt mehrere Zeilen, wenn sie Bänder hat.';

        if (unreachableCount) {
            tooltip += ' Dazu kommen ' + unreachableCount + ' Kandidaten, die die Berechnung nie '
                + 'erreicht, weil eine frühere Zeile derselben Zuordnung bereits jeden Vertrag '
                + 'abdeckt; sie stehen nicht in der Tabelle, sondern in der Zeilenaufklappung.';
        }

        if (universalFallbackCount) {
            tooltip += ' Die Zeile „alles Übrige“ am Ende des Abschnitts ist ebenfalls nicht '
                + 'mitgezählt: sie ist ein einziger Konfigurationssatz, der für jede Zuordnung '
                + 'gilt, und stand bisher unter jeder einzelnen von ihnen.';
        }

        if (zeroAssignmentCount) {
            tooltip += ' Die Kombinationen ohne Vergütung sind hier NICHT mitgezählt; sie stehen '
                + 'mit eigener Zahl unter der Tabelle.';
        }

        return tooltip;
    }

    /**
     * Applies the row filter of ONE block.
     *
     * VISIBILITY ONLY — the hard limit of this whole pane (requirement §7.6/§7.8). PrimeNG's own
     * global filter is used rather than a hand-rolled one precisely because it only ever HIDES:
     * it walks `value` in order and keeps the survivors in that same order, so the evaluation
     * order the table exists to show survives the filter untouched. Nothing is sorted, nothing is
     * recomputed and no value changes — a hidden row is hidden, never altered.
     *
     * The term is mirrored onto the block so the "X von Y" line and the empty message can tell
     * "a filter is active" from "nothing is hidden"; p-table cannot answer the first question.
     */
    applyRowFilter(block: CpConfigurationBlock, table: Table, value: string): void {
        block.filterTerm = value || '';
        table.filterGlobal(block.filterTerm, 'contains');
    }

    /** Clears the row filter of ONE block; every row of that block becomes visible again. */
    clearRowFilter(block: CpConfigurationBlock, table: Table): void {
        block.filterTerm = '';
        table.filterGlobal('', 'contains');
    }

    // =================================================================================
    // Sorting — a VISIBLE, REVERSIBLE re-ordering of the display model (client order 02.09.2026)
    // =================================================================================

    /**
     * Re-orders ONE table on a column click. Bound to `(sortFunction)` with `[customSort]="true"`.
     *
     * ── WHY `customSort` AND NOT PrimeNG'S OWN SORT
     *
     * PrimeNG sorts ROWS by a field path. This table has no atomic rows: an assignment occupies its
     * lead line, one line per further Laufzeitband, and — with the comparison switched on — one line
     * per other Vermittlergruppe (measured up to 47 lines on a single assignment). A per-line sort
     * would separate a band from the assignment whose band it is and a comparison value from the
     * value it compares against, which is not a differently ordered table but a broken one.
     *
     * ── THE ONE MECHANICAL RULE
     *
     * `event.data` IS the table's own `value` array. It has to be re-ordered IN PLACE: PrimeNG runs
     * `_filter()` immediately after this handler returns, and an array newly bound in the next
     * change-detection pass would arrive too late for it.
     */
    sortRows(block: CpConfigurationBlock, table: 'paying' | 'zero', event: SortEvent): void {

        const field: string = event?.field;
        const order: number = event?.order || 1;

        if (!block || !field || !event?.data) {
            return;
        }

        const evaluationOrder: ReadonlyArray<CpConfigurationDisplayRow> = table === 'zero'
            ? block.zeroRowsEvaluationOrder
            : block.payingRowsEvaluationOrder;

        this.reorderByAssignment(evaluationOrder, event.data as CpConfigurationDisplayRow[], block, field, order);

        const state: CpConfigurationSortState = this.buildSortState(block, field, order);

        if (table === 'zero') {
            block.zeroSort = state;
        } else {
            block.payingSort = state;
        }

        this.sortResetNotice = null;
    }

    /**
     * Puts ONE table back into the order the calculation evaluates it — the "Prüfreihenfolge" button.
     *
     * `Table.reset()` is the API that also clears the sort ARROWS (it publishes `onSort(null)` on the
     * table service, which every `p-sortIcon` listens to). It clears the FILTER values along with
     * them, which is why the block's own filter term is applied again afterwards: the filter is a
     * visibility decision of its own and has nothing to do with the order.
     */
    resetSort(block: CpConfigurationBlock, table: 'paying' | 'zero', tableRef: Table): void {

        if (!block) {
            return;
        }

        const rows: CpConfigurationDisplayRow[] = table === 'zero' ? block.zeroRows : block.payingRows;
        const evaluationOrder: ReadonlyArray<CpConfigurationDisplayRow> = table === 'zero'
            ? block.zeroRowsEvaluationOrder
            : block.payingRowsEvaluationOrder;

        tableRef?.reset();

        rows.splice(0, rows.length, ...evaluationOrder);

        if (table === 'zero') {
            block.zeroSort = null;
        } else {
            block.payingSort = null;
        }

        // Only the PAYING table has a filter. `block.filterTerm` is that table's term, so re-applying
        // it to the Nullkombinationen would hide rows nobody asked to hide.
        if (table !== 'zero' && block.filterTerm && tableRef) {
            tableRef.filterGlobal(block.filterTerm, 'contains');
        }

        this.sortResetNotice = null;
    }

    /** Whether any table of any block currently deviates from the evaluation order. */
    isAnySortActive(): boolean {
        return (this.blocks || []).some(block => !!block.payingSort || !!block.zeroSort);
    }

    /**
     * Drops PrimeNG's own sort state on every rendered table.
     *
     * Called before a reload, NOT on a user action. The `p-table` instances do not outlive the reload
     * itself — `@if (busy)` destroys the block section while the request is in flight — so this
     * covers the synchronous window before `busy` flips and any rebuild of `blocks` without a busy
     * cycle. A table that kept its `sortField` would re-run `sortSingle()` the moment a new `value`
     * arrives (`Table.onChanges`) — the sort would silently re-apply itself to rows fetched for a
     * different key date, and the chip that says so would already be gone.
     */
    protected clearTableSortState(): void {
        this.tables?.forEach(table => table.reset());
    }

    /**
     * Re-orders the display rows of one table by ASSIGNMENT, out of the frozen delivery order.
     *
     * Three properties, all of them deliberate:
     *
     *  1. **Groups, not lines.** All lines carrying the same `assignmentId` form one group and move
     *     as a block, in their delivered internal order. A band never leaves its lead line.
     *  2. **The key is the group EXTREMUM**, not the lead line's value — see {@link groupSortKey}.
     *  3. **Stability.** Equal keys keep the evaluation order, because the tie-break is the group's
     *     position in the frozen copy. Two identical factors therefore still read top-to-bottom in
     *     the order the calculation checks them.
     */
    protected reorderByAssignment(
        evaluationOrder: ReadonlyArray<CpConfigurationDisplayRow>,
        target: CpConfigurationDisplayRow[],
        block: CpConfigurationBlock,
        field: string,
        order: number): void {

        const groups: CpConfigurationDisplayRow[][] = [];
        const groupByAssignment: Map<string, CpConfigurationDisplayRow[]> = new Map();

        for (const displayRow of evaluationOrder) {

            // `rowKey` as the fallback: an assignment without an id would otherwise collapse every
            // such row into one group and shuffle unrelated lines together.
            const key: string = displayRow.assignment?.assignmentId != null
                ? 'a' + displayRow.assignment.assignmentId
                : 'k' + displayRow.rowKey;

            let group: CpConfigurationDisplayRow[] = groupByAssignment.get(key);

            if (!group) {
                group = [];
                groupByAssignment.set(key, group);
                groups.push(group);
            }

            group.push(displayRow);
        }

        const ranked: { group: CpConfigurationDisplayRow[], position: number, key: (number | string)[] }[] =
            groups.map((group, position) => ({
                group: group,
                position: position,
                key: this.groupSortKey(group, block, field, order)
            }));

        ranked.sort((left, right) => {

            // "Leer" / "alle" is not a small value, it is the most GENERAL rule — it stands at the
            // end in both directions, exactly where the evaluation order puts it.
            if (left.key === null && right.key === null) {
                return left.position - right.position;
            }
            if (left.key === null) {
                return 1;
            }
            if (right.key === null) {
                return -1;
            }

            const result: number = this.compareSortKeys(left.key, right.key);

            return result !== 0 ? order * result : left.position - right.position;
        });

        const flattened: CpConfigurationDisplayRow[] = [];

        for (const entry of ranked) {
            for (const displayRow of entry.group) {
                flattened.push(displayRow);
            }
        }

        target.splice(0, target.length, ...flattened);
    }

    /**
     * The sort key of ONE assignment: the EXTREMUM over its own lines — ascending the smallest,
     * descending the largest. `null` when the assignment says nothing about this column.
     *
     * ── WHY NOT THE LEAD LINE
     *
     * The lead line is the FIRST DELIVERED line of the assignment — own level before inherited, id
     * ascending within a level. It is neither the highest nor the lowest band, and 8.414 of the
     * 10.443 measured lines are inherited. Sorting "descending by factor" on the lead line would put
     * an assignment whose inherited line carries the highest factor of the table in the middle of the
     * small ones, and the reader who sorted precisely to find the high factors would miss it.
     *
     * ── WHY COMPARISON LINES ARE EXCLUDED
     *
     * They are ANOTHER Vermittlergruppe's values. Letting them into the extremum would sort this
     * adviser's table by numbers that are not his.
     *
     * This is a comparison, not a computation: no value is derived, summed or rounded — one of the
     * delivered values is chosen to stand for the group.
     */
    protected groupSortKey(
        group: CpConfigurationDisplayRow[],
        block: CpConfigurationBlock,
        field: string,
        order: number): (number | string)[] {

        let best: (number | string)[] = null;

        for (const displayRow of group) {

            if (displayRow.comparisonGroupName) {
                continue;
            }

            const key: (number | string)[] = this.sortKey(displayRow, block, field);

            if (key === null) {
                continue;
            }

            if (best === null) {
                best = key;
                continue;
            }

            const comparison: number = this.compareSortKeys(key, best);

            if (order > 0 ? comparison < 0 : comparison > 0) {
                best = key;
            }
        }

        return best;
    }

    /**
     * The sort key of ONE display row for ONE column — always the RAW value, never the rendered text.
     *
     * The Faktor key is the pair (Berechnungsart, Zahl) and never the number alone: 12 Monatsbeiträge
     * and 12 Promille are three orders of magnitude apart, and both modes occur in the same tables
     * (measured 8.798 rows in Monatsbeiträge against 1.645 in Promille). Every enum is read in BOTH
     * wire forms, because the endpoint sends ordinals while the DTO documents the names.
     *
     * `null` means "this row says nothing about this column" and sends the whole group to the end.
     */
    protected sortKey(
        displayRow: CpConfigurationDisplayRow,
        block: CpConfigurationBlock,
        field: string): (number | string)[] {

        if (field.indexOf(DIMENSION_SORT_FIELD_PREFIX) === 0) {

            // An assignment without a condition set ("Alle Verträge (unbedingt)") restricts
            // nothing; like an empty cell it belongs at the end rather than at the top of an
            // ascending sort.
            if (displayRow.assignment?.fallback) {
                return null;
            }

            const column: number = block.dimensionSortFields.indexOf(field);
            const value: string = column >= 0 ? displayRow.dimensionCells[column] : null;

            return value ? [value] : null;
        }

        const row: ProvisionAgentConfigurationRowDto = displayRow.row;

        switch (field) {

            case SORT_FIELD_FACTOR:
                return row && row.factor != null
                    ? [this.calculationModeRank(row), Number(row.factor)]
                    : null;

            case SORT_FIELD_TERM:
                if (!row || (row.termMonthsFrom == null && row.termMonthsTo == null)) {
                    return null;
                }
                // An open end sorts ABOVE every closed one of the same lower bound: "ab 12" covers
                // more than "12–24", and that is the order a reader of bands expects.
                return [row.termMonthsFrom != null ? row.termMonthsFrom : 0,
                    row.termMonthsTo != null ? row.termMonthsTo : Number.MAX_SAFE_INTEGER];

            case SORT_FIELD_ENTRY_AGE:
                if (!row || (row.minEntryAgeYears == null && row.maxEntryAgeYears == null)) {
                    return null;
                }
                return [row.minEntryAgeYears != null ? row.minEntryAgeYears : 0,
                    row.maxEntryAgeYears != null ? row.maxEntryAgeYears : Number.MAX_SAFE_INTEGER];

            case SORT_FIELD_LIABILITY: {
                const liability: string = row?.liabilityTypeName
                    || displayRow.assignment?.liabilityTypeOverwriteName;
                return liability ? [liability] : null;
            }

            case SORT_FIELD_AXIS:
                return row ? [this.isAgentAxis(row) ? 0 : 1] : null;

            case SORT_FIELD_ORIGIN:
                return row ? [this.originRank(row), row.originMatrixName || ''] : null;
        }

        return null;
    }

    /** MONTHLY_PREMIUM before PER_MILLE, in both wire forms. */
    protected calculationModeRank(row: ProvisionAgentConfigurationRowDto): number {
        return this.isEnumValue(row?.calculationMode, ProvisionMatrixCalcMode, ProvisionMatrixCalcMode.MONTHLY_PREMIUM)
            ? 0 : 1;
    }

    /** OWN before INHERITED, in both wire forms. */
    protected originRank(row: ProvisionAgentConfigurationRowDto): number {
        return this.isEnumValue(row?.origin, ProvisionConfigurationOrigin, ProvisionConfigurationOrigin.OWN) ? 0 : 1;
    }

    /**
     * Compares two sort keys element by element — numbers numerically, text with the German collation.
     *
     * `sensitivity: 'base'` so that "Ärzte" sorts where a German reader looks for it and case does
     * not decide anything.
     */
    protected compareSortKeys(left: (number | string)[], right: (number | string)[]): number {

        const length: number = Math.max(left.length, right.length);

        for (let index = 0; index < length; index++) {

            const leftValue: number | string = left[index];
            const rightValue: number | string = right[index];

            if (leftValue == null && rightValue == null) {
                continue;
            }
            if (leftValue == null) {
                return -1;
            }
            if (rightValue == null) {
                return 1;
            }

            const result: number = typeof leftValue === 'number' && typeof rightValue === 'number'
                ? (leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0)
                : String(leftValue).localeCompare(String(rightValue), 'de', {sensitivity: 'base'});

            if (result !== 0) {
                return result;
            }
        }

        return 0;
    }

    /**
     * The state a sorted table shows about itself — the chip text above all.
     *
     * The wording names the KEY, not only the column, wherever the key can differ from what the eye
     * sees: on a row-level column the group is ranked by its largest or smallest value, and a chip
     * that only said "sortiert nach Faktor" would leave the reader to assume it was the value on the
     * first line.
     */
    protected buildSortState(
        block: CpConfigurationBlock,
        field: string,
        order: number): CpConfigurationSortState {

        const columnLabel: string = block.sortColumnLabels?.[field] || field;

        const extremum: string = block.rowLevelSortFields?.[field]
            ? (order > 0 ? ' — kleinster Wert je Zuordnung' : ' — größter Wert je Zuordnung')
            : (order > 0 ? ' — aufsteigend' : ' — absteigend');

        let tooltip: string = block.rowLevelSortFields?.[field]
            ? 'Eine Zuordnung kann mehrere Zeilen belegen. Verglichen wird ihr '
                + (order > 0 ? 'kleinster' : 'größter') + ' eigener Wert, nicht der Wert der '
                + 'ersten Zeile — Bänder und Vergleichszeilen bleiben bei ihrer Zuordnung.'
            : 'Verglichen wird das Merkmal der Zuordnung.';

        if (field === SORT_FIELD_FACTOR) {
            // Without this sentence "12" against "8,5" reads as the larger value, although one may
            // be monthly premiums and the other per mille — three orders of magnitude apart.
            tooltip += ' Monatsbeiträge und Promille werden dabei nicht gegeneinander verrechnet: '
                + 'sie stehen als zwei getrennte Blöcke.';
        }

        return {
            field: field,
            order: order,
            columnLabel: columnLabel,
            chipLabel: 'sortiert nach „' + columnLabel + '“' + extremum
                + ' — nicht in Prüfreihenfolge',
            chipTooltip: tooltip
        };
    }

    /**
     * Whether ANY visible row of these assignments carries a Laufzeit band.
     *
     * Same data-driven principle as the dimension columns: 238 of 649 rows of the reference stock
     * carry one, so the column earns its place there — and in a component that has none it
     * disappears instead of standing empty.
     */
    protected hasTermBand(assignments: ProvisionAgentConfigurationAssignmentDto[]): boolean {
        return (assignments || []).some(assignment =>
            (assignment.rows || []).some(row => row.termMonthsFrom != null || row.termMonthsTo != null));
    }

    /** Whether any visible row carries an Eintrittsalter band. Today none does, so the column goes. */
    protected hasEntryAgeBand(assignments: ProvisionAgentConfigurationAssignmentDto[]): boolean {
        return (assignments || []).some(assignment =>
            (assignment.rows || []).some(row => row.minEntryAgeYears != null || row.maxEntryAgeYears != null));
    }

    /** Whether any visible row names a Haftungsart, or any assignment overwrites one. */
    protected hasLiability(assignments: ProvisionAgentConfigurationAssignmentDto[]): boolean {
        return (assignments || []).some(assignment =>
            !!assignment.liabilityTypeOverwriteName
            || (assignment.rows || []).some(row => !!row.liabilityTypeName));
    }

    /** German text of a Laufzeit band — "ab 12 Monate", "bis 60 Monate", "12–60 Monate". */
    termBandLabel(row: ProvisionAgentConfigurationRowDto): string {

        if (!row) {
            return '';
        }
        if (row.termMonthsFrom != null && row.termMonthsTo != null) {
            return row.termMonthsFrom + '–' + row.termMonthsTo + ' Monate';
        }
        if (row.termMonthsFrom != null) {
            return 'ab ' + row.termMonthsFrom + ' Monaten';
        }
        if (row.termMonthsTo != null) {
            return 'bis ' + row.termMonthsTo + ' Monate';
        }

        return '';
    }

    /** German text of an Eintrittsalter band. */
    entryAgeBandLabel(row: ProvisionAgentConfigurationRowDto): string {

        if (!row) {
            return '';
        }
        if (row.minEntryAgeYears != null && row.maxEntryAgeYears != null) {
            return row.minEntryAgeYears + '–' + row.maxEntryAgeYears + ' Jahre';
        }
        if (row.minEntryAgeYears != null) {
            return 'ab ' + row.minEntryAgeYears + ' Jahren';
        }
        if (row.maxEntryAgeYears != null) {
            return 'bis ' + row.maxEntryAgeYears + ' Jahre';
        }

        return '';
    }

    /**
     * True for a row on the AGENT axis — the personal row.
     *
     * Design gate FD-1, signed "nicht verhandelbar": the axis must be VISIBLE, not merely carried
     * in the DTO. Today the agent leg yields zero rows in the reference stock; on the day the first
     * personal row is created, a pane showing only the group value would quietly display one number
     * while the run pays another.
     */
    isAgentAxis(row: ProvisionAgentConfigurationRowDto): boolean {
        return this.isEnumValue(row?.axis, ProvisionConfigurationAxis, ProvisionConfigurationAxis.AGENT);
    }

    /** German period text of a group membership or a matrix row. */
    periodLabel(startDate: string, endDate: string): string {

        if (!startDate && !endDate) {
            return 'ohne Periode';
        }
        if (startDate && !endDate) {
            return 'ab ' + this.germanDate(startDate);
        }
        if (!startDate && endDate) {
            return 'bis ' + this.germanDate(endDate);
        }

        return this.germanDate(startDate) + ' – ' + this.germanDate(endDate);
    }

    /** `YYYY-MM-DD` as `DD.MM.YYYY`; anything unexpected is passed through unchanged. */
    germanDate(isoDate: string): string {

        if (!isoDate) {
            return '';
        }

        const parts: string[] = isoDate.substring(0, 10).split('-');

        return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : isoDate;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
    }

}
